<template>
    <Head :title="t('chat.detail.pageTitle')" />
    <Teleport v-if="!isBoxedPmChat" to="body">
        <div ref="mobileNavigation" class="safe-area-top-min fixed top-0 inset-x-0 px-3 flex items-center justify-between md:hidden z-30 pointer-events-none">
            <BaseButton
                theme="neutral"
                size="sm"
                :aria-label="is_development_journeys_chat ? t('chat.navigation.backTo', { destination: development_journeys_return_label }) : t('chat.navigation.goBack')"
                class="pointer-events-auto flex items-center gap-1"
                data-testid="chat-detail-back-button-mobile"
                @click="handleGoBack">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                <span class="body-small">{{ is_development_journeys_chat ? development_journeys_return_label : t("chat.navigation.back") }}</span>
            </BaseButton>
            <BaseButton
                v-if="!shouldShowSidebarControls && showWrapUp && !isBoxedPmChat"
                theme="primary"
                class="pointer-events-auto whitespace-nowrap animate-shadow-pulse"
                data-testid="chat-wrap-up-button-mobile"
                @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                {{ t("chat.detail.wrapUp") }}
            </BaseButton>
        </div>
    </Teleport>
    <PromptVersionTestBanner
        v-if="internal_version_test_banner"
        :is-published-version="internal_version_test_banner.is_published_version"
        :settings-url="internal_version_test_banner.settings_url"
        :call-code="internal_version_test_banner.call_code"
        :internal-name="internal_version_test_banner.internal_name"
        :version-label="internal_version_test_banner.version_label"
        :entered-via-entrance="internal_version_test_banner.entered_via_entrance" />
    <div
        ref="chatRow"
        class="relative flex flex-col md:flex-row grow min-h-0 overflow-hidden max-h-full text-ink bg-surface"
        :class="{ 'md:gap-5 md:px-6 md:py-5 md:justify-center': isBoxedPmChat }">
        <div
            id="chat-container"
            class="relative grow flex flex-col min-h-0 overflow-hidden max-h-full"
            :class="{ 'md:max-w-(--breakpoint-md)': isBoxedPmChat }"
            :style="composerOverlayStyle">
            <DevTools
                v-if="devtoolsEnabled"
                :messages="messages"
                :send-message="sendMessage"
                :prompt-id="first_prompt_id"
                :coaching-session-id="cs_id"
                :generate-shortcut-enabled="!chat_demo_tools" />
            <ChatWrapUpDialog
                v-model:open="isChatWrapUpDialogOpen"
                :messages="messages"
                :user-last-name="userDetails.last_name"
                :user-first-name="userDetails.first_name"
                :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                :enable-follow-up="userDetails.can_receive_emails"
                :session-feedback="session_feedback"
                :is-performance-review-session="is_performance_review_session"
                @close="handleCloseChatWrapUpDialog" />
            <BaseDialog v-model:open="isEmailSummaryDialogOpen" :title="t('chat.detail.emailSummaryDialogTitle')">
                <ChatSummary embedded :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails" />
            </BaseDialog>
            <ThoughtProcessModal :messages="messages" />
            <ReviewDocumentCardLoader
                v-if="performance_management_flow_improvements_enabled && document_upload_enabled && performance_review_context?.review_conversation_id"
                :key="`${cs_uuid}:${performance_review_context.review_conversation_id}`"
                ref="reviewDocumentCardLoader"
                :session-uuid="cs_uuid"
                :review-conversation-id="performance_review_context.review_conversation_id"
                :messages="messages"
                @allowed="reviewDocumentCardAllowed = $event"
                @ready="handleReviewDocumentCard" />
            <!-- Boxed PM chats put Wrap Up in the card's title row instead of floating it over the stream. -->
            <div v-if="!shouldShowSidebarControls && showWrapUp && !isBoxedPmChat" class="hidden md:block absolute top-10 right-7 z-10">
                <BaseButton theme="primary" class="whitespace-nowrap animate-shadow-pulse" data-testid="chat-wrap-up-button" @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                    {{ t("chat.detail.wrapUp") }}
                </BaseButton>
            </div>
            <!-- Mobile Controls and Widget List (RHS/Nadia Notes) -->
            <div
                v-if="shouldShowSidebarControls"
                :class="{ 'grow flex h-full': showWidgetSidebar }"
                class="shrink-0 md:hidden flex flex-col justify-end overflow-hidden max-h-full">
                <Teleport to="body">
                    <!-- In a boxed PM chat these controls mount in the title row instead, so they can't cover the tracker toggle (BUG-1014). -->
                    <div
                        v-if="!showWidgetSidebar && !isBoxedPmChat"
                        ref="mobileSidebarControls"
                        class="safe-area-top-min fixed top-0 right-0 -translate-x-6 items-center gap-4 md:hidden block">
                        <div class="flex justify-end">
                            <BaseButton
                                v-if="showEndRolePlay"
                                :disabled="!isEndRolePlayEnabled"
                                theme="primary"
                                class="body-small"
                                data-testid="role-play-recap-button-mobile"
                                @click="emitter.emit('end_role_play')">
                                {{ t("chat.detail.createRolePlayRecap") }}
                            </BaseButton>
                            <ChatWidgetListToggle :is-open="false" :visible-widget-count="visibleWidgetCount" @click="showWidgetSidebar = true" />
                        </div>
                        <ChatCalendarEvent
                            v-if="calendar_event"
                            class="mt-2"
                            :start-time="calendar_event.start_time"
                            :end-time="calendar_event.end_time"
                            :attendees="calendar_event.attendees"
                            :subject="calendar_event.subject" />
                    </div>
                </Teleport>
                <div v-show="showWidgetSidebar" class="overflow-y-hidden bg-white grow z-10">
                    <ChatWidgetList
                        :visible-widget-count="visibleWidgetCount"
                        :messages="messages"
                        :show-wrap-up="showWrapUp"
                        :show-end-role-play="showEndRolePlay"
                        :is-end-role-play-enabled="isEndRolePlayEnabled"
                        :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                        :nadia-notes-enabled="nadia_notes_enabled"
                        @close="showWidgetSidebar = false" />
                </div>
            </div>
            <!-- Boxed PM chat: the message stream lives in a bordered card with the review title
                 as its header row; `contents` keeps the default layout untouched otherwise. -->
            <div :class="isBoxedPmChat ? 'relative grow flex flex-col overflow-hidden min-h-0 bg-surface-raised md:rounded-2xl md:border md:border-border md:mb-3' : 'contents'">
                <div v-if="isBoxedPmChat" class="relative shrink-0 px-6 pt-5" data-testid="pm-chat-title">
                    <div class="flex flex-wrap items-center gap-3">
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            :aria-label="
                                is_development_journeys_chat ? t('chat.navigation.backTo', { destination: development_journeys_return_label }) : t('chat.navigation.goBack')
                            "
                            class="md:hidden mr-auto flex shrink-0 items-center gap-1"
                            data-testid="chat-detail-back-button-mobile"
                            @click="handleGoBack">
                            <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                            <span class="body-small">{{ is_development_journeys_chat ? development_journeys_return_label : t("chat.navigation.back") }}</span>
                        </BaseButton>
                        <h1 class="grow min-w-0 body text-ink truncate max-md:order-last max-md:basis-full">{{ pmChatTitle }}</h1>
                        <BaseButton
                            v-if="showWrapUp"
                            theme="primary"
                            size="sm"
                            class="shrink-0 whitespace-nowrap"
                            data-testid="chat-wrap-up-button-header"
                            @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                            {{ t("chat.detail.wrapUp") }}
                        </BaseButton>
                        <div class="flex shrink-0 items-center gap-3">
                            <template v-if="isMobileViewport && shouldShowSidebarControls && !showWidgetSidebar">
                                <BaseButton
                                    v-if="showEndRolePlay"
                                    :disabled="!isEndRolePlayEnabled"
                                    theme="primary"
                                    size="sm"
                                    data-testid="role-play-recap-button-mobile"
                                    @click="emitter.emit('end_role_play')">
                                    {{ t("chat.detail.createRolePlayRecap") }}
                                </BaseButton>
                                <ChatWidgetListToggle :is-open="false" :visible-widget-count="visibleWidgetCount" @click="showWidgetSidebar = true" />
                            </template>
                            <!-- On md+ the panel's own header carries its hide/show toggle. -->
                            <BaseButton
                                v-if="isMobileViewport"
                                theme="tertiary"
                                size="sm"
                                icon-only
                                class="relative z-30 shrink-0"
                                :aria-expanded="showMobileTracker"
                                :aria-controls="showMobileTracker ? 'pm-mobile-tracker' : undefined"
                                :aria-label="t('chat.detail.performanceReviewNavigation')"
                                data-testid="pm-tracker-toggle"
                                @click="showMobileTracker = !showMobileTracker">
                                <i class="bi text-xl/none" :class="showMobileTracker ? 'bi-x-lg' : 'bi-list'" aria-hidden="true"></i>
                            </BaseButton>
                        </div>
                    </div>
                    <HorizontalDivider as="hr" spacing="none" class="mt-4" />
                    <!-- Mobile only: the cycle tracker drops down from the title bar; on md+ it lives in the right-hand aside. -->
                    <template v-if="showMobileTracker">
                        <!-- FIXME: BaseFloatingPanel needs anchored mobile navigation with a click-consuming backdrop. -->
                        <div class="md:hidden fixed inset-0 z-20" aria-hidden="true" @click="showMobileTracker = false"></div>
                        <div id="pm-mobile-tracker" class="md:hidden absolute top-full inset-x-0 z-30 px-3 pt-1" data-testid="pm-mobile-tracker">
                            <PerformanceCycleTracker :context="performance_review_context" class="max-h-[65dvh] shadow-lg" />
                        </div>
                    </template>
                </div>
                <div v-if="is_development_journeys_chat" class="hidden w-full shrink-0 md:mx-auto md:flex md:max-w-(--breakpoint-md) md:px-4 md:pt-4">
                    <BaseButton
                        theme="tertiary"
                        size="sm"
                        class="inline-flex items-center gap-2"
                        data-testid="development-journeys-back-button"
                        @click="returnToDevelopmentJourneys">
                        <i class="bi bi-arrow-left" aria-hidden="true"></i>
                        {{ t("chat.navigation.backTo", { destination: development_journeys_return_label }) }}
                    </BaseButton>
                </div>
                <div v-if="workdayReviewLoading" class="w-full shrink-0 px-4 pt-3" :class="isBoxedPmChat ? 'md:px-6' : 'md:mx-auto md:max-w-(--breakpoint-md)'">
                    <ChatWorkdayReviewLoading />
                </div>
                <ChatMessageList
                    ref="chatMessageList"
                    :messages="messages"
                    :tool-call-status-text="toolCallStatusText"
                    :number-of-messages-at-init="numberOfMessagesAtInit"
                    :show-failed-response="show_failed_response"
                    :show-start-loading-message="showStartLoadingMessage"
                    :show-slow-response="show_slow_response"
                    :show-overflow="!sidebarIsTransitioning"
                    :show-widget-sidebar="showWidgetSidebar"
                    :role-play-insights-available="shouldShowSidebarControls"
                    :waiting="waiting_for_assistant"
                    :is-waiting-for-initial-response="is_waiting_for_initial_response"
                    :is-thinking="is_thinking"
                    :user-first-name="userDetails.first_name"
                    :user-last-name="userDetails.last_name"
                    :coaching-session-created-at="coaching_session_created_at"
                    :show-header="!showEndRolePlay && !performance_review_context"
                    :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                    :edit-and-stop-generating-enabled="edit_and_stop_generating_enabled && savingDevelopmentGoalUpdateMessageId === null"
                    :boxed="isBoxedPmChat"
                    :top-overlay-bottom="Math.max(mobileNavigationBottom, mobileSidebarControlsBottom)"
                    :bottom-overlay-height="composerOverlayHeight"
                    :guided-setup-new-chat-offer="activeGuidedSetupOffer"
                    :proactive-coaching-offer-enabled="talent_moment_proactive_offer_enabled"
                    :proactive-feature-offer-enabled="proactive_feature_offer_enabled"
                    :work-iq-connected="has_workiq_connected"
                    @dismiss-failure="show_failed_response = false"
                    @guided-setup-offer-dismissed="handleGuidedSetupOfferDismissed"
                    @in-chat-role-play-started="handleInChatRolePlayStarted"
                    @role-play-canceled="handleInChatRolePlayCanceled">
                    <template #tool-result="{ line, message, lineIdx }">
                        <MyDevelopmentGoalCard
                            v-if="
                                is_my_development_chat &&
                                isActionOfType(line, [ACTION.PRESENT_GOAL_DRAFT, ACTION.PRESENT_GOAL_CONTEXT, ACTION.CREATE_GOAL, ACTION.UPDATE_GOAL, ACTION.SET_GOAL_FOCUS]) &&
                                myDevelopmentGoal(message)
                            "
                            :goal="myDevelopmentGoal(message)"
                            :actions="myDevelopmentGoal(message).actions ?? []"
                            :draft="line.action_name === ACTION.PRESENT_GOAL_DRAFT"
                            :saved="line.action_name === ACTION.CREATE_GOAL" />
                        <MyDevelopmentCommittedActions
                            v-else-if="is_my_development_chat && isActionOfType(line, [ACTION.SAVE_GOAL_ACTIONS]) && myDevelopmentActionPlan(message)"
                            :plan="myDevelopmentActionPlan(message)" />
                        <div v-else-if="is_development_journeys_chat && isActionOfType(line, [ACTION.SET_UP_DEVELOPMENT_GOAL])">
                            <div role="status" aria-live="polite" aria-atomic="true" data-testid="development-goal-review-announcement">
                                <DevelopmentGoalLockIn
                                    v-if="!isDevelopmentGoalLockedIn(message, lineIdx) || hasDevelopmentGoalLockInExplanation(line)"
                                    :goal="line.action_params.development_goal"
                                    :coaching-session-uuid="cs_uuid" />
                                <MarkdownText
                                    v-if="hasDevelopmentGoalLockInExplanation(line)"
                                    class="body mx-2 -mt-2 mb-4 text-ink"
                                    :text="developmentGoalLockInExplanation(line)"
                                    data-testid="development-goal-lock-in-explanation" />
                            </div>
                            <DevelopmentGoalReview
                                v-if="isDevelopmentGoalLockedIn(message, lineIdx)"
                                :goal="line.action_params.development_goal"
                                :can-save="line === latestDevelopmentGoalAction?.line && !waiting_for_assistant && !isDevelopmentGoalLockInSaving(message, lineIdx)"
                                :is-saving="isDevelopmentGoalLockInSaving(message, lineIdx)"
                                :is-superseded="line !== latestDevelopmentGoalAction?.line"
                                :action-label="t('chat.detail.goToDevelopmentJourneys')"
                                @save="returnToDevelopmentJourneys" />
                        </div>
                        <DevelopmentGoalUpdateCard
                            v-else-if="is_development_journeys_chat && isActionOfType(line, [ACTION.PROPOSE_DEVELOPMENT_GOAL_UPDATE]) && line.action_params.goal_update"
                            :proposal="line.action_params.goal_update"
                            :saved-goal="line.action_params.saved_goal"
                            :is-active="line === activeDevelopmentGoalUpdate?.line"
                            :is-saving="savingDevelopmentGoalUpdateMessageId === message.chat_message_id" />
                    </template>
                </ChatMessageList>
            </div>
            <div ref="composerRegion" :class="composerRegionClass">
                <div v-if="activeMyDevelopmentProposal" class="w-full shrink-0 px-2 pb-2 md:mx-auto md:max-w-(--breakpoint-md) md:px-4">
                    <MyDevelopmentActionProposal
                        :key="activeMyDevelopmentProposal.message.chat_message_id"
                        :plan="activeMyDevelopmentProposal.plan"
                        :disabled="waiting_for_assistant"
                        @commit="commitMyDevelopmentActions"
                        @dismiss="dismissMyDevelopmentProposal" />
                </div>
                <DevelopmentGoalLockInActions
                    v-if="is_development_journeys_chat && activeDevelopmentGoalLockIn"
                    :disabled="waiting_for_assistant"
                    data-testid="development-goal-lock-in-actions"
                    @keep-editing="keepEditingDevelopmentGoal(activeDevelopmentGoalLockIn.message, activeDevelopmentGoalLockIn.lineIdx)"
                    @lock-in="lockInDevelopmentGoal(activeDevelopmentGoalLockIn.message, activeDevelopmentGoalLockIn.lineIdx)" />
                <DevelopmentGoalLockInActions
                    v-else-if="is_development_journeys_chat && activeDevelopmentGoalUpdate && savingDevelopmentGoalUpdateMessageId === null"
                    ref="developmentGoalUpdateActions"
                    :disabled="waiting_for_assistant"
                    :keep-editing-label="t('developmentJourneys.goalProgress.goalUpdate.cancel')"
                    :lock-in-label="t('developmentJourneys.goalProgress.goalUpdate.confirm')"
                    :keep-editing-aria-label="
                        t('developmentJourneys.goalProgress.goalUpdate.cancelLabel', { title: activeDevelopmentGoalUpdate.line.action_params.goal_update.title })
                    "
                    :lock-in-aria-label="t('developmentJourneys.goalProgress.goalUpdate.confirmLabel', { title: activeDevelopmentGoalUpdate.line.action_params.goal_update.title })"
                    data-testid="development-goal-update-actions"
                    @keep-editing="cancelDevelopmentGoalUpdate"
                    @lock-in="confirmDevelopmentGoalUpdate" />
                <!-- The card's own region arrives with its text already in it, which is not announced. -->
                <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
                <output class="sr-only" aria-live="polite" aria-atomic="true" data-testid="development-goal-update-announcement">
                    {{ developmentGoalUpdateAnnouncement }}
                </output>
                <p class="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="my-development-goal-announcement">
                    {{ myDevelopmentGoalAnnouncement }}
                </p>
                <div class="w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2">
                    <ChatNotification v-if="chatError" :message="chatError" theme="error" data-testid="chat-error" @dismiss="chatError = null" />
                    <ChatNotification v-if="chatNotice" :message="chatNotice" theme="info" data-testid="chat-notice" @dismiss="chatNotice = null" />
                    <ChatNotification v-if="chatSuccess" :message="chatSuccess" theme="success" @dismiss="chatSuccess = null" />
                    <ChatNotification v-else-if="show_failed_response" theme="error" data-testid="chat-failed-response" @dismiss="show_failed_response = false">
                        <I18nT keypath="chat.detail.failedResponse.message" tag="div" class="body-small-semibold">
                            <template #refresh>
                                <div>
                                    <BaseButton theme="tertiary" size="sm" @click="handleFailedResponseReload">{{ t("chat.detail.failedResponse.refresh") }}</BaseButton>
                                </div>
                            </template>
                        </I18nT>
                    </ChatNotification>
                    <BaseNotification v-if="fileUploadLimitBanner" theme="error" @dismiss="fileUploadLimitBanner = false">
                        <template #content>
                            <p class="body-small-semibold">
                                {{ t("chat.detail.fileUploadLimit.message", { limit: FILE_UPLOAD_LIMIT }) }}
                            </p>
                            <BaseButton theme="tertiary" size="sm" @click="openUrl($startChatUrl, { queryParams: { chat_source: CHATSOURCE.FILE_UPLOAD_LIMIT } })">{{
                                t("chat.detail.fileUploadLimit.startNewChat")
                            }}</BaseButton>
                        </template>
                    </BaseNotification>
                </div>
                <!-- Announced rather than focused: a dialog would interrupt the conversation, which is
                     more than a suggestion like this earns. The region cannot live in the banner,
                     because the Transition below unmounts it on chat errors and dismissed offers and a
                     region recreated with its text already in it is not announced. -->
                <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">
                    {{ teamsBannerVisible ? t("chat.detail.teamsBannerAnnouncement") : "" }}
                </p>
                <Transition v-if="!hasErrorBanner && !activeGuidedSetupOffer" name="slide-up" mode="out-in">
                    <div v-if="voiceNudgeAction && !isNadiaChatRefreshEnabled" class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-6">
                        <ChatVoiceNudge :content="voiceNudgeAction.line.action_params.content" @dismiss="dismissVoiceNudge" @activate-voice="activateVoiceFromToast" />
                    </div>
                    <MSTeamsChatBanner
                        v-else-if="!voiceNudgeAction"
                        :org-has-ms-teams="org_has_ms_teams && teams_nudges_enabled"
                        :ms-teams-app-url="ms_teams_app_url"
                        :user-message-count="userMessageCount"
                        :coaching-session-id="cs_id"
                        :is-suppressed="is_ms_teams_chat_banner_suppressed"
                        @visibility-change="teamsBannerVisible = $event" />
                </Transition>
                <div class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-6">
                    <PreferredLanguageNotice />
                </div>
                <ReturnToChatControls v-if="show_return_to_chat_controls && !returnToChatChoiceMade" @continue-chat="handleContinueChat" />
                <div
                    :class="[
                        isBoxedPmChat
                            ? 'w-full px-2 md:px-0'
                            : isNadiaChatRefreshEnabled
                              ? 'w-screen md:w-full md:max-w-[calc(var(--breakpoint-md)_+_2rem)] md:mx-auto px-2'
                              : 'w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto px-6',
                        isNadiaChatRefreshEnabled ? 'mb-4' : isBoxedPmChat ? 'mb-2' : 'mb-2 lg:mb-6',
                    ]">
                    <!-- On md+ these live in the right-hand panel while it is open. -->
                    <div v-show="showInChatPmActions">
                        <ChatDraftManagerReviewButton
                            v-if="showManagerReviewButton"
                            :latest-message-id="latestMessageId"
                            :subject-name="performance_review_context?.subject_name" />
                        <ChatDraftSelfReviewButton v-if="showSelfReviewButton" :latest-message-id="latestMessageId" />
                        <ChatGoalPushButton v-if="showGoalPushButton" :latest-message-id="latestMessageId" />
                    </div>
                    <ChatMessageControls
                        v-if="!rolePlaySetupActive"
                        ref="chatMessageControls"
                        :upload-context-key="`${cs_uuid}:${performance_review_context?.review_conversation_id ?? ''}`"
                        textarea-max-length="5000"
                        :location="CHAT_MESSAGE_CONTROLS_LOCATION.CHAT_DETAIL"
                        :is-sending="isBufferingMessage"
                        :composer-mentions-enabled="!showEndRolePlay"
                        :is-transcribing="waiting_for_transcript"
                        :is-disabled="
                            showStartLoadingMessage ||
                            isPerformancePickerOpen ||
                            isDevelopmentGoalChoicePending ||
                            savingDevelopmentGoalUpdateMessageId !== null ||
                            reviewDocumentUploadBusy
                        "
                        :show-slow-transcription="show_slow_transcription"
                        :default-text-input="defaultTextInput"
                        :user-message-count="messages.filter((m) => m.role === 'user').length"
                        :document-upload-enabled="document_upload_enabled"
                        :visual-document-analysis-enabled="visual_document_analysis_enabled"
                        :work-document-capabilities-url="work_document_capabilities_url"
                        :better-voice-composer-enabled="better_voice_composer_enabled"
                        :is-file-upload-limit="fileUploadCount + useChatStore.files.length >= FILE_UPLOAD_LIMIT"
                        :user-coach-exchange-id="userCoachExchangeId"
                        :pii-patterns="pii_patterns"
                        :waiting-for-assistant="waiting_for_assistant"
                        :edit-and-stop-generating-enabled="edit_and_stop_generating_enabled && savingDevelopmentGoalUpdateMessageId === null"
                        :has-workiq-connected="has_workiq_connected"
                        :placeholder="is_development_journeys_chat ? t('chat.detail.modifyGoalPlaceholder') : undefined"
                        :show-voice-nudge="showVoiceNudgeCoachMark"
                        @voice-nudge-activate="activateVoiceFromNudge"
                        @voice-nudge-dismiss="dismissVoiceNudge"
                        @recording-complete="handleRecordingComplete"
                        @recording-error="handleRecordingError"
                        @recording-start="handleRecordingStart"
                        @textbox-show="handleTextboxShown"
                        @text-keydown="handleKeyDown"
                        @text-send="handleSendMessage"
                        @team-created="router.reload({ only: ['taggable_teams'], preserveState: true, preserveScroll: true })"
                        @text-change="composerHasDraft = $event.length > 0"
                        @stop-generating="requestStopGenerating"
                        @upload-error="(errorMsg) => (chatError = errorMsg)"
                        @upload-notice="(notice) => (chatNotice = notice)" />
                </div>
                <div class="flex justify-center" :class="isNadiaChatRefreshEnabled ? 'mb-5' : isBoxedPmChat ? 'mb-2 mt-1' : 'mb-2 lg:-mt-4'">
                    <Disclaimer :message="disclaimer_message" />
                </div>
            </div>
        </div>
        <aside
            v-if="openArtifactId && !showArtifactAsSheet"
            class="relative hidden md:flex h-full shrink-0 overflow-hidden border-l border-border"
            :class="{ 'select-none': isArtifactPaneResizing }"
            :style="artifactPaneStyle"
            data-testid="artifact-pane">
            <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- WAI-ARIA window-splitter: a focusable separator is the accessible resize control -->
            <div
                role="separator"
                aria-orientation="vertical"
                :aria-label="t('artifacts.panel.resize')"
                :aria-valuemin="artifactPaneMinWidth"
                :aria-valuemax="artifactPaneMaxWidth"
                :aria-valuenow="artifactPaneWidth"
                tabindex="0"
                class="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize focus-ring hover:bg-border-strong focus-visible:bg-border-strong"
                :class="{ 'bg-border-strong': isArtifactPaneResizing }"
                data-testid="artifact-pane-resize"
                @pointerdown="onArtifactPanePointerDown"
                @keydown="onArtifactPaneKeydown"></div>
            <ChatArtifactPanel
                :artifact-id="openArtifactId"
                :version-number="openArtifactVersion"
                :refresh-key="artifactRefreshKey"
                :expanded="isArtifactPaneExpanded"
                class="w-full"
                @toggle-expand="toggleArtifactPaneExpand"
                @close="closeArtifact" />
        </aside>
        <!-- The dialog header keeps the only Close, so it stays reachable however far a long document is scrolled. -->
        <BaseDialog v-if="artifacts_enabled" v-model:open="isArtifactDialogOpen" :title="t('artifacts.document')" mobile-height="expanded">
            <ChatArtifactPanel
                v-if="openArtifactId"
                :artifact-id="openArtifactId"
                :version-number="openArtifactVersion"
                :refresh-key="artifactRefreshKey"
                hide-expand
                hide-close
                class="h-[80dvh]"
                @close="closeArtifact" />
        </BaseDialog>
        <aside v-if="shouldShowSidebarControls" class="hidden md:flex h-full overflow-hidden" :class="{ 'min-w-81.25': showWidgetSidebar }">
            <ChatWidgetSidebar
                :is-transitioning="sidebarIsTransitioning"
                :visible-widget-count="visibleWidgetCount"
                :role-play-adjustments="role_play_adjustments"
                :role-play-adjustment-default="role_play_adjustment_default"
                :show-widget-list="showWidgetSidebar"
                :show-wrap-up="showWrapUp"
                :show-end-role-play="showEndRolePlay"
                :is-end-role-play-enabled="isEndRolePlayEnabled"
                :messages="messages"
                :has-feedback-report="hasReport"
                :calendar-event="calendar_event"
                :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                :nadia-notes-enabled="nadia_notes_enabled"
                @transition-start="sidebarIsTransitioning = true"
                @transition-end="sidebarIsTransitioning = false"
                @toggle-show-widget-list="handleToggleWidgetSidebar" />
        </aside>
        <aside v-else-if="isBoxedPmChat && performance_review_context" ref="pmTrackerAside" class="hidden md:flex shrink-0 flex-col overflow-hidden max-h-full">
            <!-- v-show keeps the tracker mounted, so reopening the panel neither refetches nor re-logs a view. -->
            <div v-show="showDesktopTracker" id="pm-tracker-panel" class="flex w-80 min-h-0 flex-col gap-3">
                <div v-if="showManagerReviewButton || showSelfReviewButton || showGoalPushButton" class="flex flex-col gap-2" data-testid="pm-sidebar-actions">
                    <ChatDraftManagerReviewButton
                        v-if="showManagerReviewButton"
                        :latest-message-id="latestMessageId"
                        :subject-name="performance_review_context?.subject_name"
                        placement="sidebar" />
                    <ChatDraftSelfReviewButton v-if="showSelfReviewButton" :latest-message-id="latestMessageId" placement="sidebar" />
                    <ChatGoalPushButton v-if="showGoalPushButton" :latest-message-id="latestMessageId" placement="sidebar" />
                </div>
                <PerformanceCycleTracker :context="performance_review_context">
                    <template #action>
                        <BaseButton
                            ref="pmTrackerHideButton"
                            theme="ghost"
                            size="sm"
                            icon-only
                            :aria-label="t('chat.detail.hidePanel')"
                            :title="t('chat.detail.hidePanel')"
                            aria-expanded="true"
                            aria-controls="pm-tracker-panel"
                            data-testid="pm-tracker-hide"
                            @click="setDesktopTrackerOpen(false)">
                            <i class="bi bi-layout-sidebar-inset-reverse text-xl/none" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                </PerformanceCycleTracker>
            </div>
            <BaseButton
                v-if="!showDesktopTracker"
                ref="pmTrackerShowButton"
                theme="neutral"
                size="sm"
                icon-only
                :aria-label="t('chat.detail.showPanel')"
                :title="t('chat.detail.showPanel')"
                aria-expanded="false"
                aria-controls="pm-tracker-panel"
                data-testid="pm-tracker-show"
                @click="setDesktopTrackerOpen(true)">
                <i class="bi bi-layout-sidebar-reverse text-xl/none" aria-hidden="true"></i>
            </BaseButton>
        </aside>
    </div>
    <ChatDemoTools
        :tools="chat_demo_tools"
        :proactive-offer-url="proactive_offer_demo_url"
        :prompt-id="first_prompt_id"
        :connections="connections"
        :disabled="waiting_for_assistant || rolePlaySetupActive || isPerformancePickerOpen || isDevelopmentGoalChoicePending"
        :composer-available="!rolePlaySetupActive"
        :composer-has-draft="composerHasDraft"
        @offer-generated="reloadAfterDemoOffer" />
    <CookieBanner />
    <BaseToast v-model:open="isChatToastOpen" :duration="CHAT_TOAST_DURATION_MS" :truncate-length="CHAT_TOAST_MAX_LENGTH" :description="chatToastMessage" />
</template>

<script>
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";

export default {
    layout: LeftSidebarLayout,
};
</script>

<script setup>
/**
 * Mirrors the `_PromptVersionTestBanner` dataclass in chat/views.py. Present only for a staff
 * prompt-version test chat; null otherwise (the banner renders iff this is non-null).
 * @typedef {object} PromptVersionTestBanner
 * @property {boolean} is_published_version
 * @property {string} settings_url
 * @property {string} call_code
 * @property {string} internal_name
 * @property {string} version_label
 * @property {boolean} entered_via_entrance
 */
import { convertToDateTime } from "/js/dateUtils";
import { getCookie } from "/js/utils.js";
import { Head, router } from "@inertiajs/vue3";
import { useElementBounding, useElementSize, useEventListener, useMediaQuery, useStorage } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import { ACTION, getNotesCount, isActionOfType, messageHasActions, pluckAction, pluckMultipleActions } from "~vue/chatActions.js";
import ChatArtifactPanel from "~vue/ChatDetail/artifacts/ChatArtifactPanel.vue";
import ChatCalendarEvent from "~vue/ChatDetail/ChatCalendarEvent.vue";
import ChatDemoTools from "~vue/ChatDetail/ChatDemoTools.vue";
import ChatDraftManagerReviewButton from "~vue/ChatDetail/ChatDraftManagerReviewButton.vue";
import ChatDraftSelfReviewButton from "~vue/ChatDetail/ChatDraftSelfReviewButton.vue";
import ChatGoalPushButton from "~vue/ChatDetail/ChatGoalPushButton.vue";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import ChatSummary from "~vue/ChatDetail/ChatSummary.vue";
import ChatVoiceNudge from "~vue/ChatDetail/ChatVoiceNudge.vue";
import ChatWorkdayReviewLoading from "~vue/ChatDetail/ChatWorkdayReviewLoading.vue";
import ChatWrapUpDialog from "~vue/ChatDetail/ChatWrapUpDialog.vue";
import DevelopmentGoalLockIn from "~vue/ChatDetail/DevelopmentGoalLockIn.vue";
import DevelopmentGoalLockInActions from "~vue/ChatDetail/DevelopmentGoalLockInActions.vue";
import DevelopmentGoalReview from "~vue/ChatDetail/DevelopmentGoalReview.vue";
import { developmentGoalUpdateQuestion, saveDevelopmentGoalUpdate } from "~vue/ChatDetail/developmentGoalUpdate";
import DevelopmentGoalUpdateCard from "~vue/ChatDetail/DevelopmentGoalUpdateCard.vue";
import MyDevelopmentActionProposal from "~vue/ChatDetail/MyDevelopmentActionProposal.vue";
import MyDevelopmentCommittedActions from "~vue/ChatDetail/MyDevelopmentCommittedActions.vue";
import MyDevelopmentGoalCard from "~vue/ChatDetail/MyDevelopmentGoalCard.vue";
import PerformanceCycleTracker from "~vue/ChatDetail/PerformanceCycleTracker.vue";
import ReviewDocumentCardLoader from "~vue/ChatDetail/ReviewDocumentCardLoader.vue";
import { taggableTeamsKey } from "~vue/ChatDetail/taggedTeam";
import { createPerformancePickerGate, performancePickerGateKey } from "~vue/ChatDetail/usePerformancePickerGate";
import ChatMessageList from "~vue/ChatMessageList.vue";
import ChatNotification from "~vue/ChatNotification.vue";
import ChatWidgetList from "~vue/ChatWidgetList.vue";
import ChatWidgetListToggle from "~vue/ChatWidgetListToggle.vue";
import ChatWidgetSidebar from "~vue/ChatWidgetSidebar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import MSTeamsChatBanner from "~vue/components/MSTeamsChatBanner.vue";
import PreferredLanguageNotice from "~vue/components/PreferredLanguageNotice.vue";
import PromptVersionTestBanner from "~vue/components/PromptVersionTestBanner.vue";
import ReturnToChatControls from "~vue/components/ReturnToChatControls.vue";
import { useChatInputPreference } from "~vue/composables/useChatInputPreference.js";
import { useCollaborationExplorations } from "~vue/composables/useCollaborationExplorations";
import { useDevelopmentGoalSave } from "~vue/composables/useDevelopmentGoalSave";
import { useKeyboard } from "~vue/composables/useKeyboard";
import { useLeaveConfirmation } from "~vue/composables/useLeaveConfirmation";
import { useNadiaChatRefreshEnabled } from "~vue/composables/useNadiaChatRefresh";
import { useResizablePaneWidth } from "~vue/composables/useResizablePaneWidth";
import { useSendStreamingEvent } from "~vue/composables/useSendStreamingEvent";
import { useStatusBar } from "~vue/composables/useStatusBar";
import { useWorkdayReviewLoading } from "~vue/composables/useWorkdayReviewLoading";
import DevTools from "~vue/DevTools.vue";
import Disclaimer from "~vue/Disclaimer.vue";
import { CHAT_EVENT } from "~vue/events";
import MarkdownText from "~vue/MarkdownText.vue";
import { createRealtimeClient } from "~vue/RealtimeClientFactory.js";
import { useChatStore } from "~vue/stores/chatStore";
import ThoughtProcessModal from "~vue/ThoughtProcessModal.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION, CHATSOURCE, FILE_UPLOAD_LIMIT } from "~vue/utils/constants";
import { createWebSocketErrorHandler, logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getWafErrorMessage, WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import { DateTime } from "luxon";
import { computed, inject, nextTick, onMounted, onUnmounted, provide, reactive, ref, useTemplateRef, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

import { transcribeRecording } from "../../transcription.js";
import { getRandomInt } from "../utils/random.js";

useStatusBar();
const { t } = useI18n();

const userDetails = inject("user_details");
const organizationDetails = inject("organization_details");
const { emitter, $setEventContext, $sendEvent, $sendSocketEvent, $feedbackMailto } = inject("globalProperties");

const ONBOARDING_TASK = {
    ROLE: "action_onboarding_role",
    MIC: "action_onboarding_mic",
    FOLLOWUP: "action_onboarding_followup",
    GUIDANCE: "action_onboarding_guidance",
    BOOKMARK: "action_onboarding_bookmark",
};

const SLOW_MESSAGE_TIMEOUT_MS = 2000;
const FAILED_MESSAGE_TIMEOUT_MS = 55000;

const chatErrorMessages = computed(() => ({
    TRANSCRIPTION_EXHAUSTED: t("chat.detail.errors.transcriptionExhausted"),
    RECORDING_DEFAULT: t("chat.detail.errors.recordingDefault"),
    SEND_FAILURE: t("chat.detail.errors.sendFailure"),
    NO_SPEECH_DETECTED: t("chat.detail.errors.noSpeechDetected"),
}));

const autoOpenTextInputErrors = computed(() => [chatErrorMessages.value.TRANSCRIPTION_EXHAUSTED, chatErrorMessages.value.RECORDING_DEFAULT, chatErrorMessages.value.SEND_FAILURE]);

const {
    is_development_journeys_chat,
    is_my_development_chat,
    development_journeys_url,
    development_journeys_return_label,
    role_play_adjustment_default,
    role_play_adjustments,
    event_context,
    stream_url,
    is_start_loading_message_enabled,
    disclaimer_message,
    cs_id,
    cs_uuid,
    chat_source,
    coaching_session_created_at,
    first_prompt_id,
    dev_tools_enabled,
    document_upload_enabled,
    visual_document_analysis_enabled,
    work_document_capabilities_url,
    role_play: initialRolePlay,
    feedback_report,
    connections,
    memory_connections_enabled,
    calendar_event,
    is_dogfood_instance,
    show_return_to_chat_controls,
    session_feedback,
    org_has_ms_teams,
    ms_teams_app_url,
    teams_nudges_enabled,
    public_profile_enabled,
    collaboration_analysis_enabled,
    collaboration_profile_invite_url,
    is_ms_teams_chat_banner_suppressed,
    pii_patterns,
    taggable_teams,
    better_voice_composer_enabled,
    nadia_notes_enabled,
    artifacts_enabled,
    edit_and_stop_generating_enabled,
    performance_management_flow_improvements_enabled,
    is_performance_review_session,
    performance_review_context,
    in_chat_pm_enabled,
    internal_version_test_banner,
    has_workiq_connected,
    guided_setup_new_chat_offer,
    talent_moment_proactive_offer_enabled,
    proactive_feature_offer_enabled,
    proactive_offer_demo_url,
    chat_demo_tools,
    workday_context_refresh_url,
    workday_review_status_url,
} = defineProps({
    is_development_journeys_chat: { type: Boolean, default: false },
    is_my_development_chat: { type: Boolean, default: false },
    development_journeys_url: { type: String, default: "" },
    development_journeys_return_label: { type: String, default: "" },
    role_play_adjustment_default: { type: String, default: "" },
    role_play_adjustments: { type: Array, default: () => [] },
    event_context: { type: Object, default: () => ({}) },
    stream_url: { type: String, default: "" },
    is_start_loading_message_enabled: { type: Boolean, default: false },
    disclaimer_message: { type: String, default: "" },
    cs_id: { type: String, default: "" },
    cs_uuid: { type: String, default: null },
    chat_source: { type: String, default: null },
    coaching_session_created_at: { type: String, required: true },
    first_prompt_id: { type: Number, required: true },
    dev_tools_enabled: { type: Boolean },
    default_chat_redirect_url: { type: String, default: "/" },
    document_upload_enabled: { type: Boolean, default: false },
    visual_document_analysis_enabled: { type: Boolean, default: false },
    work_document_capabilities_url: { type: String, default: "" },
    role_play: { type: Object, default: undefined },
    feedback_report: { type: Object, default: () => ({}) },
    connections: { type: Array, default: () => [] },
    memory_connections_enabled: { type: Boolean, default: false },
    calendar_event: { type: Object, default: undefined },
    is_dogfood_instance: { type: Boolean, default: false },
    show_return_to_chat_controls: { type: Boolean, default: false },
    session_feedback: { type: Object, default: null },
    org_has_ms_teams: { type: Boolean, default: false },
    ms_teams_app_url: { type: String, default: "" },
    teams_nudges_enabled: { type: Boolean, default: false },
    public_profile_enabled: { type: Boolean, default: false },
    collaboration_analysis_enabled: { type: Boolean, default: false },
    collaboration_profile_invite_url: { type: String, default: "" },
    is_ms_teams_chat_banner_suppressed: { type: Boolean, default: false },
    pii_patterns: { type: Array, default: null },
    taggable_teams: { type: Array, default: () => [] },
    better_voice_composer_enabled: { type: Boolean, default: false },
    nadia_notes_enabled: { type: Boolean, default: false },
    artifacts_enabled: { type: Boolean, default: false },
    edit_and_stop_generating_enabled: { type: Boolean, default: false },
    performance_management_flow_improvements_enabled: { type: Boolean, default: false },
    is_performance_review_session: { type: Boolean, default: false },
    performance_review_context: {
        /** @type {import('vue').PropType<import('~vue/types/performance_management').PerformanceReviewContext | null>} */
        type: Object,
        default: null,
    },
    in_chat_pm_enabled: { type: Boolean, default: false },
    internal_version_test_banner: {
        /** @type {import('vue').PropType<PromptVersionTestBanner | null>} */
        type: Object,
        default: null,
    },
    has_workiq_connected: { type: Boolean, default: false },
    guided_setup_new_chat_offer: {
        /** @type {import('vue').PropType<import('~vue/ChatDetail/types').GuidedSetupNewChatOffer | null>} */
        type: Object,
        default: null,
    },
    talent_moment_proactive_offer_enabled: { type: Boolean, default: false },
    proactive_feature_offer_enabled: { type: Boolean, default: false },
    proactive_offer_demo_url: { type: String, default: null },
    chat_demo_tools: {
        /** @type {import('vue').PropType<import('~vue/ChatDetail/ChatDemoTools.vue').ChatDemoToolsConfig | null>} */
        type: Object,
        default: null,
    },
    workday_context_refresh_url: { type: String, default: null },
    workday_review_status_url: { type: String, default: null },
});

const reloadAfterDemoOffer = () => globalThis.location.reload();

// Re-reads the conversation's Workday context server-side after the page loads, so opening a chat
// never waits on Workday (LAB-1268), and again when the tab comes back, since Workday edits are usually
// made in another tab (LAB-2002). The server holds repeat reads off, so calling often is safe. Nothing on
// the page depends on the answer.
const { execute: refreshWorkdayContext } = useFetch(workday_context_refresh_url || "", { immediate: false }).post();
// While the Workday review is read in the background the chat says so (LAB-1789). Asked after the refresh, which
// may be what queues the read.
const { reviewLoading: workdayReviewLoading, check: checkWorkdayReviewLoading } = useWorkdayReviewLoading(workday_review_status_url);

provide("coachingSessionId", cs_id);
provide("coachingSessionUuid", cs_uuid);
provide(
    "connections",
    computed(() => connections),
);
provide("publicProfileEnabled", public_profile_enabled);
provide(
    taggableTeamsKey,
    computed(() => taggable_teams),
);
provide("collaborationAnalysisEnabled", collaboration_analysis_enabled);
provide("artifactsEnabled", artifacts_enabled);

// Artifact side pane (desktop) / expanded bottom-sheet dialog (mobile), opened from artifact cards.
const openArtifactId = ref(null);
// The version the clicked card is pinned to; null means show the latest. A card keeps the version
// it was written for (v1 card stays v1 even after v3 exists), so it must survive the trip to the pane.
const openArtifactVersion = ref(null);
const artifactRefreshKey = ref(0);
// Same rem-based breakpoint as the aside's `md:` classes, so a larger browser font cannot leave a
// viewport where the pane is hidden by CSS while the sheet is suppressed by this flag.
const isDesktopViewport = useMediaQuery("(min-width: 48rem)");
const isMobileViewport = computed(() => !isDesktopViewport.value);
// The row the pane shares with the chat column (the app's left navigation sits outside it);
// the chat column keeps at least this much, else the document opens as a sheet instead.
const chatRow = ref(null);
const { width: chatRowWidth } = useElementSize(chatRow);
const MIN_CHAT_COLUMN_WIDTH = 440;
// The boxed PM chat's tracker aside shares the row: the full panel when open, just its show button when collapsed.
const pmTrackerAside = ref(null);
const { width: pmTrackerReservedWidth } = useElementSize(pmTrackerAside);
const {
    width: artifactPaneWidth,
    minWidth: artifactPaneMinWidth,
    maxWidth: artifactPaneMaxWidth,
    fits: artifactPaneFits,
    isExpanded: isArtifactPaneExpanded,
    isResizing: isArtifactPaneResizing,
    style: artifactPaneStyle,
    toggleExpand: toggleArtifactPaneExpand,
    onPointerDown: onArtifactPanePointerDown,
    onKeydown: onArtifactPaneKeydown,
} = useResizablePaneWidth({
    storageKey: "nadia-artifact-pane-width",
    defaultWidth: 520,
    minWidth: 360,
    maxViewportFraction: 0.66,
    availableWidth: chatRowWidth,
    reservedWidth: () => MIN_CHAT_COLUMN_WIDTH + pmTrackerReservedWidth.value,
});
const showArtifactAsSheet = computed(() => isMobileViewport.value || !artifactPaneFits.value);
const isArtifactDialogOpen = computed({
    get: () => artifacts_enabled && showArtifactAsSheet.value && openArtifactId.value !== null,
    set: (open) => {
        if (!open) {
            openArtifactId.value = null;
            openArtifactVersion.value = null;
        }
    },
});
const handleOpenArtifact = ({ artifactId, versionNumber = null }) => {
    if (!artifacts_enabled) {
        return;
    }
    if (openArtifactId.value === artifactId) {
        artifactRefreshKey.value += 1;
    }
    openArtifactVersion.value = versionNumber;
    openArtifactId.value = artifactId;
    showWidgetSidebar.value = false;
};
const handleArtifactUpdated = ({ artifactId }) => {
    if (openArtifactId.value === artifactId) {
        // A new version was just saved: drop any card version pin so the pane jumps to the latest.
        openArtifactVersion.value = null;
        artifactRefreshKey.value += 1;
    }
};
const closeArtifact = () => {
    openArtifactId.value = null;
    openArtifactVersion.value = null;
};
// Calls that put a document on screen for the user: a write, or show_artifact re-surfacing an existing one.
const ARTIFACT_SURFACE_ACTIONS = [ACTION.CREATE_ARTIFACT, ACTION.EDIT_ARTIFACT, ACTION.REWRITE_ARTIFACT, ACTION.SHOW_ARTIFACT];
// A document Nadia just wrote, revised or re-surfaced opens on its own, like Claude/ChatGPT. Do not auto-open
// whenever the pane would be a sheet (mobile, or a chat row too narrow for both columns); the sheet would cover
// the chat, so the card stays the way in. The card's result arrives on the role="tool" message
// (chat_stream_service._handle_tool_calls). Historical cards (initialize_chat) never pass through here.
const openArtifactFromLiveMessage = (message) => {
    if (!artifacts_enabled || message.role === "user" || !Array.isArray(message.lines)) {
        return;
    }
    const line = message.lines.findLast(
        (l) => l.type === "action" && ARTIFACT_SURFACE_ACTIONS.includes(l.action_name) && l.action_params?.artifact?.artifact_id && !l.action_params.error,
    );
    if (!line) {
        return;
    }
    const id = line.action_params.artifact.artifact_id;
    if (openArtifactId.value === id) {
        if (line.action_name === ACTION.SHOW_ARTIFACT) {
            openArtifactVersion.value = null;
            artifactRefreshKey.value += 1;
        }
        return;
    }
    if (showArtifactAsSheet.value) {
        return;
    }
    // A document Nadia just wrote opens at its latest (freshly written) version.
    openArtifactId.value = id;
    openArtifactVersion.value = null;
    showWidgetSidebar.value = false;
};
provide("collaborationProfileInviteUrl", collaboration_profile_invite_url);
provide("feedbackReport", feedback_report);

const CHAT_TOAST_DURATION_MS = 10000;
const CHAT_TOAST_MAX_LENGTH = 200;
const isChatToastOpen = ref(false);
const chatToastMessage = ref("");
const showChatToast = (message) => {
    chatToastMessage.value = message;
    isChatToastOpen.value = true;
};
provide("showChatToast", showChatToast);

const sendStreamingEvent = stream_url ? useSendStreamingEvent(stream_url, emitter, event_context) : null;
provide("sendStreamingEvent", sendStreamingEvent);

const messages = ref([]);
let chatPageActive = true;
const reviewDocumentCardLoader = useTemplateRef("reviewDocumentCardLoader");
const reviewDocumentCardAllowed = ref(false);
const activePerformancePicker = computed(() => {
    const lastUser = messages.value.findLastIndex((message) => message.role === "user");
    const latest = pluckMultipleActions(messages.value.slice(lastUser + 1), ACTION.PM_PICKER).at(-1);
    return latest ? `${latest.message.chat_message_id}:${latest.line.index}` : null;
});
const performancePickerGate = createPerformancePickerGate(
    () => performance_management_flow_improvements_enabled,
    () => activePerformancePicker.value,
);
const isPerformancePickerOpen = performancePickerGate.isOpen;
provide(performancePickerGateKey, performancePickerGate);
provide(
    "performanceManagementFlowImprovements",
    computed(() => performance_management_flow_improvements_enabled),
);
const myDevelopmentGoalAnnouncement = ref("");
provide("collaborationExploredConnections", useCollaborationExplorations(messages));
const lockedInDevelopmentGoalActions = ref(new Set());
const lockingInDevelopmentGoalActions = ref(new Set());
const dismissedDevelopmentGoalActions = ref(new Set());
const myDevelopmentGoal = (message) => {
    const content = message.lines.find((line) => line.type === "md")?.content;
    if (!content) {
        return null;
    }
    try {
        return JSON.parse(content).goal ?? null;
    } catch {
        return null;
    }
};
const myDevelopmentActionPlan = (message) => {
    const content = message.lines?.find((line) => line.type === "md")?.content;
    if (!content) {
        return null;
    }
    try {
        const plan = JSON.parse(content).action_plan;
        return plan && Array.isArray(plan.actions) && plan.week_start ? plan : null;
    } catch {
        return null;
    }
};
const dismissedMyDevelopmentProposals = ref(new Set());
const isMyDevelopmentProposalExpired = (plan) => {
    const expiryDate = DateTime.fromISO(plan.week_start, { zone: "UTC" }).plus({ days: 7 }).toISODate();
    const userTime = DateTime.utc().setZone(userDetails?.value?.timezone?.trim() || "UTC");
    const today = (userTime.isValid ? userTime : DateTime.utc()).toISODate();
    return !expiryDate || today >= expiryDate;
};
const activeMyDevelopmentProposal = computed(() => {
    if (!is_my_development_chat) {
        return null;
    }
    for (const message of [...messages.value].reverse()) {
        if (message.role === "user") {
            return null;
        }
        if (message.role === "tool" && message.lines?.some((line) => isActionOfType(line, [ACTION.SAVE_GOAL_ACTIONS])) && myDevelopmentActionPlan(message)) {
            return null;
        }
        if (message.role === "tool" && message.lines?.some((line) => isActionOfType(line, [ACTION.RECOMMEND_GOAL_ACTIONS]))) {
            const plan = myDevelopmentActionPlan(message);
            return plan && !isMyDevelopmentProposalExpired(plan) && !dismissedMyDevelopmentProposals.value.has(message.chat_message_id) ? { message, plan } : null;
        }
    }
    return null;
});
const dismissMyDevelopmentProposal = () => {
    if (activeMyDevelopmentProposal.value) {
        dismissedMyDevelopmentProposals.value = new Set(dismissedMyDevelopmentProposals.value).add(activeMyDevelopmentProposal.value.message.chat_message_id);
        void nextTick(() => chatMessageControls.value?.focus());
    }
};
const commitMyDevelopmentActions = (actions) => {
    const proposal = activeMyDevelopmentProposal.value;
    if (!proposal || waiting_for_assistant.value || !actions.length) {
        return;
    }
    if (isMyDevelopmentProposalExpired(proposal.plan)) {
        dismissMyDevelopmentProposal();
        return;
    }
    const list = actions
        .map((action) => `- ${action.title}${action.timing ? ` (${action.timing})` : ""} · ${t(action.with_nadia ? "myDevelopment.withNadia" : "myDevelopment.onYourOwn")}`)
        .join("\n");
    const content = proposal.plan.goal_title
        ? t("myDevelopment.commitActionsMessage", { goal: proposal.plan.goal_title, actions: list })
        : t("myDevelopment.commitActionsMessageNoGoal", { actions: list });
    if (sendMessage(content)) {
        void nextTick(() => chatMessageControls.value?.focus());
    }
};
const announceMyDevelopmentGoal = async (message) => {
    if (!is_my_development_chat || message.role !== "tool") {
        return;
    }
    const line = message.lines?.find((item) =>
        isActionOfType(item, [ACTION.PRESENT_GOAL_DRAFT, ACTION.PRESENT_GOAL_CONTEXT, ACTION.CREATE_GOAL, ACTION.UPDATE_GOAL, ACTION.SET_GOAL_FOCUS]),
    );
    const actionLine = message.lines?.find((item) => isActionOfType(item, [ACTION.RECOMMEND_GOAL_ACTIONS, ACTION.SAVE_GOAL_ACTIONS]));
    const actionPlan = actionLine && myDevelopmentActionPlan(message);
    if (actionPlan) {
        myDevelopmentGoalAnnouncement.value = "";
        await nextTick();
        myDevelopmentGoalAnnouncement.value =
            actionLine.action_name === ACTION.SAVE_GOAL_ACTIONS
                ? t("myDevelopment.committedActions")
                : actionPlan.goal_title
                  ? t("myDevelopment.actionProposalFor", { goal: actionPlan.goal_title })
                  : t("myDevelopment.actionProposal");
        return;
    }
    const goal = line && myDevelopmentGoal(message);
    if (!goal?.title) {
        return;
    }
    const label =
        line.action_name === ACTION.PRESENT_GOAL_DRAFT
            ? t("myDevelopment.goalDraft")
            : line.action_name === ACTION.CREATE_GOAL
              ? t("myDevelopment.goalSaved")
              : goal.is_focused
                ? t("myDevelopment.focusedGoal")
                : t("myDevelopment.savedGoal");
    myDevelopmentGoalAnnouncement.value = "";
    await nextTick();
    myDevelopmentGoalAnnouncement.value = `${label}: ${goal.title}`;
};
const isDevelopmentGoalDraftMessage = (message) => messageHasActions(message, [ACTION.SET_UP_DEVELOPMENT_GOAL]);
const isDevelopmentGoalUpdateCardMessage = (message) =>
    (message.lines ?? []).some((line) => line.type === "action" && line.action_name === ACTION.PROPOSE_DEVELOPMENT_GOAL_UPDATE && line.action_params?.goal_update);
const isTeamPickerMessage = (message) =>
    (message.lines ?? []).some((line) => line.type === "action" && line.action_name === ACTION.GET_TEAM_PERSPECTIVE && line.action_params?.status === "available");
const DEVELOPMENT_GOAL_CARD_ACTION = {
    LOCK_IN: "lock_in",
    KEEP_EDITING: "keep_editing",
};
const latestDevelopmentGoalAction = computed(() => {
    for (const message of [...messages.value].reverse()) {
        for (let lineIdx = (message.lines ?? []).length - 1; lineIdx >= 0; lineIdx -= 1) {
            const line = message.lines[lineIdx];
            if (line.type === "action" && line.action_name === ACTION.SET_UP_DEVELOPMENT_GOAL) {
                return { message, line, lineIdx };
            }
        }
    }

    return null;
});

const developmentGoalActionKey = (message, lineIdx) => {
    const messageKey = message.chat_message_id ?? message.message_id ?? message.id ?? messages.value.indexOf(message);

    return `${messageKey}:${lineIdx}`;
};

const developmentGoalsMatch = (left, right) =>
    left?.title === right?.title && left?.achievement_criteria === right?.achievement_criteria && left?.target_date === right?.target_date;

const hasPersistedDevelopmentGoalLockIn = (message, lineIdx) => {
    const sourceLine = message.lines[lineIdx];
    const sourceGoal = sourceLine?.action_params?.development_goal;

    return message.lines.some((line) => {
        if (line.type !== "action" || line.action_name !== ACTION.DEVELOPMENT_GOAL_LOCKED_IN) {
            return false;
        }

        const sourceActionIndex = line.action_params?.source_action_index;
        if (sourceActionIndex != null && sourceLine?.index != null) {
            return sourceActionIndex === sourceLine.index;
        }

        return developmentGoalsMatch(line.action_params?.development_goal, sourceGoal);
    });
};

const isDevelopmentGoalLockedIn = (message, lineIdx) =>
    lockedInDevelopmentGoalActions.value.has(developmentGoalActionKey(message, lineIdx)) || hasPersistedDevelopmentGoalLockIn(message, lineIdx);
const isDevelopmentGoalDismissed = (message, lineIdx) => dismissedDevelopmentGoalActions.value.has(developmentGoalActionKey(message, lineIdx));
const isDevelopmentGoalLockInSaving = (message, lineIdx) => lockingInDevelopmentGoalActions.value.has(developmentGoalActionKey(message, lineIdx));
const hasDevelopmentGoalLockInExplanation = (line) => Boolean(line.action_params?.lock_in_explanation);
const developmentGoalLockInExplanation = (line) => line.action_params.lock_in_explanation;
const hasUserMessageAfterDevelopmentGoal = (message) => {
    const messageIdx = messages.value.indexOf(message);
    return messageIdx !== -1 && messages.value.slice(messageIdx + 1).some((laterMessage) => laterMessage.role === "user");
};

const dismissDevelopmentGoalLockIn = (message, lineIdx) => {
    dismissedDevelopmentGoalActions.value = new Set(dismissedDevelopmentGoalActions.value).add(developmentGoalActionKey(message, lineIdx));
};

const activeDevelopmentGoalLockIn = computed(() => {
    const latestAction = latestDevelopmentGoalAction.value;

    if (
        !latestAction ||
        isDevelopmentGoalLockedIn(latestAction.message, latestAction.lineIdx) ||
        isDevelopmentGoalDismissed(latestAction.message, latestAction.lineIdx) ||
        hasUserMessageAfterDevelopmentGoal(latestAction.message)
    ) {
        return null;
    }

    return latestAction;
});

const isDevelopmentGoalLockInPending = computed(() => is_development_journeys_chat && !!activeDevelopmentGoalLockIn.value);
const isDevelopmentGoalChoicePending = computed(() => isDevelopmentGoalLockInPending.value || (is_development_journeys_chat && lockingInDevelopmentGoalActions.value.size > 0));

// A draft goal or its in-flight lock-in has not been persisted yet, so leaving that phase silently loses the goal.
useLeaveConfirmation(t("chat.detail.leaveGoalConfirmation"), isDevelopmentGoalChoicePending);

const logDevelopmentGoalCardAction = (action) => {
    void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GOAL_CARD_ACTION, { development_journeys_goal_card_action: action }, cs_uuid);
};

const lockInDevelopmentGoal = async (message, lineIdx) => {
    const line = message.lines[lineIdx];
    if (line !== latestDevelopmentGoalAction.value?.line || waiting_for_assistant.value || isDevelopmentGoalLockInSaving(message, lineIdx)) {
        return;
    }

    logDevelopmentGoalCardAction(DEVELOPMENT_GOAL_CARD_ACTION.LOCK_IN);

    const actionKey = developmentGoalActionKey(message, lineIdx);
    lockingInDevelopmentGoalActions.value = new Set(lockingInDevelopmentGoalActions.value).add(actionKey);
    lockedInDevelopmentGoalActions.value = new Set(lockedInDevelopmentGoalActions.value).add(actionKey);

    let goalWasSaved = false;

    try {
        goalWasSaved = await saveDevelopmentGoal(line.action_params.development_goal);
        if (!goalWasSaved) {
            return;
        }

        const action = {
            chat_message_id: message.chat_message_id,
            type: ACTION.DEVELOPMENT_GOAL_LOCKED_IN,
            action_params: {
                development_goal: line.action_params.development_goal,
                source_action_index: line.index ?? lineIdx,
            },
            action_state: {
                dismissed: false,
                minimized: false,
                submitted: true,
            },
        };

        if (sendStreamingEvent) {
            const persisted = await sendStreamingEvent("create_action", action);
            if (!persisted) {
                throw new Error("The goal lock-in action was not saved.");
            }
        } else {
            await $sendEvent("create_action", action);
        }
    } catch {
        if (goalWasSaved) {
            chatError.value = t("chat.detail.errors.goalSavedNotLocked");
        } else if (!chatError.value) {
            chatError.value = t("chat.detail.errors.goalLockInFailed");
        }
    } finally {
        if (!goalWasSaved) {
            const lockedActions = new Set(lockedInDevelopmentGoalActions.value);
            lockedActions.delete(actionKey);
            lockedInDevelopmentGoalActions.value = lockedActions;
        }

        const lockingActions = new Set(lockingInDevelopmentGoalActions.value);
        lockingActions.delete(actionKey);
        lockingInDevelopmentGoalActions.value = lockingActions;
    }
};

const returnToDevelopmentJourneys = () => {
    if (development_journeys_url) {
        router.visit(development_journeys_url);
    }
};

// The manager-review draft button attaches the card to the newest message, so it lands at the bottom.
const latestMessageId = computed(() => messages.value[messages.value.length - 1]?.chat_message_id);
// Live progress text for in-flight tool calls, keyed by tool_call_id (see handleToolCallStatus).
// A reactive Map (not a ref) so ChatMessageList's `toolCalls` computed re-runs on individual
// key updates without needing to replace the whole Map on every status tick.
const toolCallStatusText = reactive(new Map());
const waiting_for_transcript = ref(false);
const waiting_for_assistant = ref(false);
const show_slow_response = ref(false);
const show_failed_response = ref(false);
const show_slow_transcription = ref(false);
const has_taken_action = ref(false);
const is_waiting_for_initial_response = ref(true);
const is_thinking = ref(false);
const slow_response_timeout = ref(null);
const failed_response_timeout = ref(null);
const backgrounded_at = ref(null);
const rolePlayAdjust = ref(null);
const showStartLoadingMessage = ref(is_start_loading_message_enabled);
const startLoadingMessageTimeout = ref(null);
const firstSocketMessageReceived = ref(false);
const numberOfMessagesAtInit = ref(0);
const showWidgetSidebar = ref(false);
const sidebarIsTransitioning = ref(false);
const chatError = ref(null);
const chatNotice = ref(null);
const chatSuccess = ref(null);
const promptSwitchCallCode = ref(null);
const socketClient = ref(null);
const devtoolsEnabled = ref(dev_tools_enabled || import.meta.env.DEV);
const composerHasDraft = ref(false);
const dictated_in_this_message = ref(false);
const isChatWrapUpDialogOpen = ref(false);
const isEmailSummaryDialogOpen = ref(false);
const isInitialized = ref(false);
const userCoachExchangeId = ref(crypto.randomUUID());
const returnToChatChoiceMade = ref(false);

const { saveDevelopmentGoal } = useDevelopmentGoalSave({
    coachingSessionUuid: cs_uuid,
    setError: (message) => {
        chatError.value = message;
    },
});

const resetAssistantResponseState = () => {
    waiting_for_assistant.value = false;
    is_waiting_for_initial_response.value = false;
    is_thinking.value = false;
    clearSlowResponseTimeout();
};

// RAF throttling for streaming updates to reduce CPU overhead
let partialUpdateRafId = null;

const fileUploadLimitBanner = ref(false);
const teamsBannerVisible = ref(false);

const hasErrorBanner = computed(() => !!chatError.value || show_failed_response.value || fileUploadLimitBanner.value);
const fileUploadCount = computed(() => {
    const fileUploadActions = pluckMultipleActions(messages.value, ACTION.FILE_UPLOAD);

    // Params flagged `skipped` are files the backend rejected (e.g. over the size
    // limit) — they render on the card but never consumed an upload slot.
    return fileUploadActions.reduce((acc, action) => acc + action.line.action_params.filter((p) => !p.skipped).length, 0);
});
const isFileUploadLimit = computed(() => fileUploadCount.value >= FILE_UPLOAD_LIMIT);

const rolePlaySetupActive = computed(() => {
    const rolePlaySetupActions = pluckMultipleActions(messages.value, ACTION.ROLE_PLAY_SETUP);
    if (!rolePlaySetupActions.length) {
        return false;
    }

    const latestSetupAction = rolePlaySetupActions[rolePlaySetupActions.length - 1];
    if (latestSetupAction.line.action_state.submitted || latestSetupAction.line.action_state.dismissed) {
        return false;
    }

    const setupMessageIndex = messages.value.findIndex((message) => message.chat_message_id === latestSetupAction.message.chat_message_id);
    const laterMessages = messages.value.slice(setupMessageIndex + 1);
    const hasLaterConversationContent = laterMessages.some((message) =>
        message.lines.some(
            (line) => (line.type === "md" && !line.hidden && line.content.trim().length > 0) || (line.type === "action" && line.action_name === ACTION.ROLE_PLAY_SUMMARY),
        ),
    );

    return !hasLaterConversationContent;
});

watch(
    isFileUploadLimit,
    (newVal) => {
        if (newVal) {
            fileUploadLimitBanner.value = true;
            logUserInteraction("file_upload_limit_reached", {}, cs_uuid);
        }
    },
    { immediate: true },
);

const showWrapUp = computed(() => messages.value.filter((m) => m.role === "user").length >= 2 && !useChatStore.rolePlay?.hashid);

const showEndRolePlay = computed(() => !!useChatStore.rolePlay?.hashid && !useChatStore.rolePlay?.finished_at);
const isEndRolePlayEnabled = computed(() => {
    const rolePlayMonitorActions = pluckMultipleActions(messages.value, ACTION.ROLE_PLAY_MONITOR);
    return rolePlayMonitorActions.filter((action) => action.line.action_params.role_play_hashid === useChatStore.rolePlay.hashid).length > 1;
});

const visibleWidgetCount = computed(() => {
    return getNotesCount(messages.value);
});

const hasActiveRolePlay = computed(() => {
    const rolePlay = useChatStore.rolePlay;
    // Use `hashid` (not `id`) to detect an active role play: on page-load hydration the
    // role play is serialized via RolePlay.to_dict() → model_to_dict(), which drops the
    // non-editable integer `id` PK but keeps `hashid`. Keying off `id` here made the sidebar
    // (and the recap button inside it) unmount on reload during an active role play (BUG-627).
    return !!rolePlay?.hashid && rolePlay?.finished_at == undefined;
});

const hasRolePlayContent = computed(() => hasActiveRolePlay.value || !!pluckAction(messages.value, ACTION.ROLE_PLAY_MONITOR));

// Dedicated PM chats give the right-hand rail to the cycle tracker instead of the widget
// sidebar — except while a role play is live, when the sidebar is the context: it holds the
// insights "View suggestion" opens and the End Role Play button that produces the recap
// card and report (BUG-1014).
const shouldShowSidebarControls = computed(
    () => (nadia_notes_enabled || hasRolePlayContent.value) && !(is_performance_review_session && in_chat_pm_enabled && !hasActiveRolePlay.value),
);

// Boxed PM layout: messages in a bordered card with a title row, cycle tracker on the right.
const isBoxedPmChat = computed(() => is_performance_review_session && in_chat_pm_enabled && !!performance_review_context);
// Only where a goal push could succeed, and once there's a message to anchor the action to.
const showGoalPushButton = computed(() => performance_review_context?.goal_push_available === true && latestMessageId.value != null);
const showManagerReviewButton = computed(() => performance_review_context?.manager_review_push_available === true && latestMessageId.value != null);
const showSelfReviewButton = computed(
    () =>
        performance_review_context?.self_review_draft_enabled === true &&
        performance_review_context?.is_self === true &&
        performance_review_context?.moment_type === "end_of_year" &&
        latestMessageId.value != null,
);
// Below md the tracker has no room beside the chat, so a title-bar toggle drops it down;
// on md+ the right-hand aside collapses to its show button, and that choice is remembered.
// Tailwind's `md` is 48rem, so a px breakpoint would drift from the layout under a larger browser font size.
const showMobileTracker = ref(false);
const showDesktopTracker = useStorage("pmChatTrackerOpen", true);
// Each toggle hides the button that was clicked, so focus moves to its replacement
// rather than dropping to the page for a keyboard user.
const pmTrackerHideButton = useTemplateRef("pmTrackerHideButton");
const pmTrackerShowButton = useTemplateRef("pmTrackerShowButton");
async function setDesktopTrackerOpen(open) {
    showDesktopTracker.value = open;
    await nextTick();
    (open ? pmTrackerHideButton : pmTrackerShowButton).value?.$el.focus();
}
// On md+ a boxed PM chat's actions live only in the tracker panel: hiding the panel does not
// hoist them back above the composer (LAB-2203), so the collapsed state is a clean chat.
// Below md the panel is an overlay with no actions of its own, so in-chat is the only entry point.
const showInChatPmActions = computed(() => !isBoxedPmChat.value || !isDesktopViewport.value);
// The cycle (moment) name — which review period you're in. The specific task and person
// are the tracker's job: it highlights the row for this very chat.
const pmChatTitle = computed(() => performance_review_context?.cycle ?? "");

const userMessageCount = computed(() => {
    return messages.value.filter((m) => m.role === "user").length;
});

// New Chat offers own the banner slot until dismissed; voice and Teams nudges wait.
const guidedSetupOfferDismissed = ref(false);
const activeGuidedSetupOffer = computed(() => (guidedSetupOfferDismissed.value ? null : guided_setup_new_chat_offer));

async function handleGuidedSetupOfferDismissed() {
    guidedSetupOfferDismissed.value = true;
    await nextTick();
    emitter.emit(CHAT_EVENT.OPEN_TEXT_INPUT);
}

const voiceNudgeDismissed = ref(false);
const voiceNudgeAction = computed(() => {
    // Suppresses nudges already persisted on past messages; new ones are blocked server-side
    // in chat.widget_tools.voice_nudge.
    if (voiceNudgeDismissed.value || organizationDetails?.value?.is_chat_voice_input_hidden) {
        return null;
    }
    for (const message of messages.value) {
        for (const line of message.lines) {
            if (line.type === "action" && line.action_name === ACTION.VOICE_NUDGE && !line.action_state?.dismissed) {
                return { line, lineIdx: message.lines.indexOf(line), message };
            }
        }
    }
    return null;
});

// Marks the server-side action seen so the one-shot nudge never returns. Shared by both exits:
// activating (tapping the mic) and dismissing (the toast's close button, or any other first interaction
// while the coach-mark shows).
function persistVoiceNudgeSeen() {
    if (!voiceNudgeAction.value) {
        return;
    }
    const { message, line } = voiceNudgeAction.value;
    voiceNudgeDismissed.value = true;
    emitter.emit("voice_nudge", {
        message_id: message.chat_message_id,
        lineIdx: message.lines.indexOf(line),
        action_state: { dismissed: true },
    });
}

function dismissVoiceNudge() {
    if (!voiceNudgeAction.value) {
        return;
    }
    persistVoiceNudgeSeen();
    logUserInteraction(AmplitudeEventNames.VOICE_NUDGE_DISMISSED, {}, cs_uuid);
}

// The coach-mark's mic button opens voice input on its own click, so this only records the activation
// and retires the nudge; the toast path opens voice input via activateVoiceFromToast.
function activateVoiceFromNudge() {
    if (!voiceNudgeAction.value) {
        return;
    }
    persistVoiceNudgeSeen();
    logUserInteraction(AmplitudeEventNames.VOICE_NUDGE_MIC_ACTIVATED, {}, cs_uuid);
}

function activateVoiceFromToast() {
    emitter.emit(CHAT_EVENT.OPEN_VOICE_INPUT);
    activateVoiceFromNudge();
}

const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();

// The refreshed composer shows the nudge as a coach-mark on the mic; the legacy one keeps the toast.
// Same suppression as the toast's banner slot: errors and New Chat offers take priority.
const showVoiceNudgeCoachMark = computed(() => isNadiaChatRefreshEnabled.value && !!voiceNudgeAction.value && !hasErrorBanner.value && !activeGuidedSetupOffer.value);

// The refreshed full-page chat pins the composer region as an opaque overlay so the stream
// scrolls behind it. Its height is published as `--composer-overlay-h` for ChatMessageList's
// bottom padding and scroll-to-latest rail. The overlay itself is desktop-only (`md:`).
// At every size the region is `sticky bottom-0`: it lays out in flow normally, but if
// over-tall column content (banners, notices, a viewport glitch) would push it below the
// clipped bottom edge, it sticks at the column's visible bottom instead — BUG-1212,
// where the mounted composer was painted off-screen.
const composerRegion = useTemplateRef("composerRegion");
const composerRegionHeight = ref(0);
let composerResizeObserver = null;
watch(
    composerRegion,
    (el) => {
        composerResizeObserver?.disconnect();
        composerResizeObserver = null;
        if (el) {
            composerResizeObserver = new ResizeObserver((entries) => {
                composerRegionHeight.value = entries[0]?.contentRect.height ?? 0;
            });
            composerResizeObserver.observe(el);
        }
    },
    { immediate: true, flush: "post" },
);
onUnmounted(() => composerResizeObserver?.disconnect());
const useComposerOverlay = computed(() => isNadiaChatRefreshEnabled.value && !isBoxedPmChat.value);
const composerRegionClass = computed(() =>
    useComposerOverlay.value ? "composer-region sticky bottom-0 md:absolute md:inset-x-0 md:z-20 md:bg-surface" : "composer-region sticky bottom-0",
);
const composerOverlayHeight = computed(() => (useComposerOverlay.value ? Math.ceil(composerRegionHeight.value) : 0));
const composerOverlayStyle = computed(() => (composerOverlayHeight.value > 0 ? { "--composer-overlay-h": `${composerOverlayHeight.value}px` } : undefined));

// Use stored user preference, fallback to text input (winning experiment result)
const userChatInputPreference = useChatInputPreference();
const defaultTextInput = computed(() => {
    return userChatInputPreference.value !== null ? userChatInputPreference.value : true;
});

watch(chatError, (current, old) => {
    if (current && !old && autoOpenTextInputErrors.value.includes(current)) {
        emitter.emit(CHAT_EVENT.OPEN_TEXT_INPUT);
    }
});

const cycleUserCoachExchangeId = () => {
    userCoachExchangeId.value = crypto.randomUUID();
    return userCoachExchangeId.value;
};

onMounted(() => {
    $setEventContext(event_context);
    if (workday_context_refresh_url) {
        refreshWorkdayContext().finally(checkWorkdayReviewLoading);
    } else {
        checkWorkdayReviewLoading();
    }
    if (stream_url) {
        const cid = new URLSearchParams(window.location.search).get("cid");
        socketClient.value = createRealtimeClient(stream_url, {
            reconnectEnabled: true,
            reconnectInterval: 2000,
        });
        socketClient.value.connect();
        let firstMessageReceived = false;

        socketClient.value.onMessage = (msg) => {
            const message_data = JSON.parse(msg.data);
            if (!firstMessageReceived) {
                firstMessageReceived = true;
                emitter.emit("first_socket_message");
            }
            if (message_data.type) {
                emitter.emit(message_data.type, message_data);
            }
        };
        socketClient.value.onOpen = async () => {
            if (reviewDocumentCardLoader.value) {
                await reviewDocumentCardLoader.value.prepare();
            }
            if (!chatPageActive) {
                return;
            }
            if (!isInitialized.value) {
                if (cid) {
                    socketClient.value.nextCorrelationId = cid;
                }
                $sendSocketEvent(
                    "initialize_chat",
                    {
                        dictation_on: useChatStore.dictationOn,
                        uce_id: cycleUserCoachExchangeId(),
                    },
                    socketClient.value,
                );
            }
        };
        socketClient.value.onClose = (closeEvent) => {
            resetAssistantResponseState();
            if (closeEvent.code === 4401) {
                chatError.value = t("chat.detail.errors.loggedOut");
            } else {
                chatError.value = t("chat.detail.errors.connection");
            }
        };
        socketClient.value.onError = createWebSocketErrorHandler({ cs_id, userCoachExchangeId }, "ChatDetail", (e) => {
            resetAssistantResponseState();
            // The session-ended dialog is already up and says the same thing, only accurately --
            // it knows whether the user needs a reload or a fresh login (ENG-1667).
            if (isSessionEndedError(e)) {
                return;
            }
            if (e?.cause === WAF_ERROR_CAUSE) {
                chatError.value = getWafErrorMessage($feedbackMailto);
            } else if (e?.status === 403) {
                chatError.value = t("chat.detail.errors.sessionOutOfDate");
            } else {
                chatError.value = t("chat.detail.errors.connection");
            }
        });
    }

    if (initialRolePlay) {
        useChatStore.setRolePlay(initialRolePlay);
    }

    logUserInteraction("first_paint", {}, cs_uuid);

    // NOTE: Every listener added here must have a corresponding "off" call in onUmounted().
    emitter.on("chat_message", handleChatMessage);
    emitter.on("chat_warning", handleChatWarning);
    emitter.on("chat_error", handleChatError);
    emitter.on("transcription_event", handleTranscriptionEvent);
    emitter.on("chat_partial_addition", handleChatPartialAddition);
    emitter.on("tool_call_status", handleToolCallStatus);
    emitter.on("initialize_chat", handleChatInit);
    emitter.on("choose_followup", handleChooseFollowup);
    emitter.on("followup_update", handleFollowupChange);
    emitter.on("profile_question", handleProfileQuestion);
    emitter.on(ACTION.ROLE_PLAY_ADJUST, handleRolePlayAdjust);
    emitter.on("action_items", handleActionItems);
    emitter.on("voice_nudge", handleVoiceNudge);
    emitter.on(ACTION.PM_PICKER, handlePmPicker);
    emitter.on("first_socket_message", handleFirstSocketMessage);
    emitter.on("onboarding_followup_scheduled", handleOnboardingFollowup);
    emitter.on(CHAT_EVENT.WRAP_UP_CHAT, handleWrapUpChat);
    emitter.on(CHAT_EVENT.OPEN_SESSION_SUMMARY, handleOpenEmailSummary);
    emitter.on("pii_rejected", handlePiiRejected);
    emitter.on("error_notification", handleErrorNotification);
    emitter.on("success_notification", handleSuccessNotification);
    emitter.on("role_play_started", handleRolePlayStarted);
    emitter.on(CHAT_EVENT.EDIT_MESSAGE, handleEditMessage);
    emitter.on(CHAT_EVENT.STOP_GENERATING, handleStopGenerating);
    emitter.on("end_role_play", handleEndRolePlay);
    emitter.on("initial_response_started", handleInitialResponseStarted);
    emitter.on("chat_send_deduplicated", handleChatSendDeduplicated);
    emitter.on("thinking_started", handleThinkingStarted);
    emitter.on("visible_content_started", handleVisibleContentStarted);
    emitter.on(CHAT_EVENT.OPEN_ARTIFACT, handleOpenArtifact);
    emitter.on(CHAT_EVENT.ARTIFACT_UPDATED, handleArtifactUpdated);
    emitter.on("chat_message_retracted", handleChatMessageRetracted);
    emitter.on("chat_message_updated", handleChatMessageUpdated);
});

onUnmounted(() => {
    chatPageActive = false;
    $setEventContext({});
    emitter.emit(CHAT_EVENT.LEAVING_CHAT);
    if (socketClient.value) {
        socketClient.value.removeListeners();
        socketClient.value.instance?.close();
    }

    // Cancel any pending RAF for streaming updates
    if (partialUpdateRafId) {
        cancelAnimationFrame(partialUpdateRafId);
        partialUpdateRafId = null;
    }

    useChatStore.setRolePlay({});

    emitter.off(CHAT_EVENT.OPEN_ARTIFACT, handleOpenArtifact);
    emitter.off(CHAT_EVENT.ARTIFACT_UPDATED, handleArtifactUpdated);
    emitter.off("chat_message", handleChatMessage);
    emitter.off("chat_warning", handleChatWarning);
    emitter.off("chat_error", handleChatError);
    emitter.off("transcription_event", handleTranscriptionEvent);
    emitter.off("chat_partial_addition", handleChatPartialAddition);
    emitter.off("tool_call_status", handleToolCallStatus);
    emitter.off("initialize_chat", handleChatInit);
    emitter.off("choose_followup", handleChooseFollowup);
    emitter.off("followup_update", handleFollowupChange);
    emitter.off("profile_question", handleProfileQuestion);
    emitter.off(ACTION.ROLE_PLAY_ADJUST, handleRolePlayAdjust);
    emitter.off("action_items", handleActionItems);
    emitter.off("voice_nudge", handleVoiceNudge);
    emitter.off(ACTION.PM_PICKER, handlePmPicker);
    emitter.off("first_socket_message", handleFirstSocketMessage);
    emitter.off("onboarding_followup_scheduled", handleOnboardingFollowup);
    emitter.off(CHAT_EVENT.WRAP_UP_CHAT, handleWrapUpChat);
    emitter.off(CHAT_EVENT.OPEN_SESSION_SUMMARY, handleOpenEmailSummary);
    emitter.off("pii_rejected", handlePiiRejected);
    emitter.off("error_notification", handleErrorNotification);
    emitter.off("success_notification", handleSuccessNotification);
    emitter.off("role_play_started", handleRolePlayStarted);
    emitter.off(CHAT_EVENT.EDIT_MESSAGE, handleEditMessage);
    emitter.off(CHAT_EVENT.STOP_GENERATING, handleStopGenerating);
    emitter.off("end_role_play", handleEndRolePlay);
    emitter.off("initial_response_started", handleInitialResponseStarted);
    emitter.off("chat_send_deduplicated", handleChatSendDeduplicated);
    emitter.off("thinking_started", handleThinkingStarted);
    emitter.off("visible_content_started", handleVisibleContentStarted);
    emitter.off("chat_message_retracted", handleChatMessageRetracted);
    emitter.off("chat_message_updated", handleChatMessageUpdated);
});

const hasReport = computed(() => {
    return feedback_report && Object.keys(feedback_report).length > 0;
});

const clearLoadingMessageTimeout = () => {
    if (startLoadingMessageTimeout.value !== null) {
        window.clearTimeout(startLoadingMessageTimeout.value);
        startLoadingMessageTimeout.value = null;
    }
};

useEventListener(document, "visibilitychange", () => {
    const eventAt = new Date();
    const now = eventAt.toISOString();
    const eventName = document.visibilityState === "hidden" ? "backgrounded_tab" : "resumed_tab";
    const uce_id = userCoachExchangeId.value;

    let shouldReload = false;
    let minutesSpentBackgrounded = null;
    if (eventName === "backgrounded_tab") {
        backgrounded_at.value = eventAt;
    } else if (backgrounded_at.value) {
        minutesSpentBackgrounded = (eventAt - backgrounded_at.value) / 1000 / 60;
        shouldReload = minutesSpentBackgrounded > 8 * 60;
    }

    window?.Sentry?.addBreadcrumb({
        message: "ChatDetail received 'visibilitychange' event.",
        data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded, shouldReload },
    });

    // A reload refreshes on mount anyway.
    if (eventName === "resumed_tab" && !shouldReload && workday_context_refresh_url) {
        refreshWorkdayContext().finally(checkWorkdayReviewLoading);
    }

    new Promise((resolve, reject) => {
        logUserInteraction(eventName, { uce_id, now }, cs_uuid)
            .then(resolve)
            .catch((err) => {
                const message = `Failed to send '${eventName}' event.  Will wait a few seconds and retry.`;
                console.warn(message, err);
                window?.Sentry?.addBreadcrumb({
                    type: "error",
                    category: "error",
                    level: "warning",
                    message,
                    data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded, shouldReload, err },
                });
                setTimeout(() => {
                    logUserInteraction(eventName, { uce_id, now }, cs_uuid)
                        .then(resolve)
                        .catch((err) => {
                            const message = `Failed to send '${eventName}' event after a second try: ${err}`;
                            console.error(message);
                            window?.Sentry?.addBreadcrumb({
                                type: "error",
                                category: "error",
                                level: "error",
                                message,
                                data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded, shouldReload, err },
                            });
                            reject(new Error(message));
                        });
                }, 2000);
            });
    }).finally(() => {
        if (shouldReload) {
            window?.Sentry?.addBreadcrumb({
                message: "Reloading document.",
                data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded },
            });
            document.location.reload();
        }
    });
});

useEventListener(window, "beforeunload", () => {
    logUserInteraction("chat_unload", {}, cs_uuid);
});

const handleFirstSocketMessage = () => {
    firstSocketMessageReceived.value = true;
    /*
     * Clear the loading message threshold in case we received a
     * message through the socket before the loading message timeout
     * runs. This avoids showing the loading message unnecessarily.
     */
    clearLoadingMessageTimeout();
};

const handleChatInit = (data) => {
    messages.value = data.data.messages;
    numberOfMessagesAtInit.value = data.data.messages.length;
    scrollToBottom();

    // If navigating from search results (?msg=<id>), scroll to the matched message
    const searchMsgId = Number(new URLSearchParams(window.location.search).get("msg"));
    if (searchMsgId && !isNaN(searchMsgId)) {
        nextTick(() => {
            emitter.emit(CHAT_EVENT.FOCUS_CHAT_MESSAGE, { chatMessageId: searchMsgId });
        });
    }

    // Initialize role play adjustment if last message has a
    // role_play_adjustment action.
    if (messages.value.length > 0) {
        const lastRolePlayAdjust = pluckAction([messages.value[messages.value.length - 1]], ACTION.ROLE_PLAY_ADJUST, true);
        if (lastRolePlayAdjust && lastRolePlayAdjust?.line.action_params?.adjustment) {
            rolePlayAdjust.value = lastRolePlayAdjust.line.action_params.adjustment;
        }
    }

    // For new conversations with no visible user messages, the backend will automatically
    // stream a first message. Set waiting state so the thinking indicators show.
    const visibleUserMessages = messages.value.filter((m) => m.role === "user" && !m.hidden);
    const assistantMessages = messages.value.filter((m) => m.role === "assistant");
    const hasDevelopmentGoalDraft = messages.value.some(isDevelopmentGoalDraftMessage);

    if (visibleUserMessages.length === 0 && assistantMessages.length === 0 && !hasDevelopmentGoalDraft) {
        // No visible user messages and no assistant messages yet - waiting for first response
        is_waiting_for_initial_response.value = true;
        waiting_for_assistant.value = true;
    } else if (messages.value.length > 0) {
        // If the last message is from the user, we're waiting for an assistant response
        // This handles the case when starting a chat from homepage with initial_message
        const lastMessage = messages.value[messages.value.length - 1];
        if (lastMessage.role === "user" && !lastMessage.hidden) {
            is_waiting_for_initial_response.value = true;
            waiting_for_assistant.value = true;
        } else {
            is_waiting_for_initial_response.value = false;
            if (hasDevelopmentGoalDraft) {
                waiting_for_assistant.value = false;
            }
        }
    }

    // Store initialized state so that we don't reinitialize if we disconnect and reconnect
    isInitialized.value = true;
};

const handleTextboxShown = () => {
    has_taken_action.value = true;
};

const getMessageById = (message_id, temp_id = undefined) => {
    for (let m of messages.value) {
        // TODO: Clean up the keys here
        if (m.id == message_id || m.message_id == message_id || m.chat_message_id == message_id) {
            return m;
        }
        if (temp_id && (m.temp_id == temp_id || m.message_id == temp_id)) {
            return m;
        }
    }
    return false;
};

const getMessageLine = (message_id, lineIdx) => {
    const message = getMessageById(message_id);
    if (message) {
        return message.lines[lineIdx];
    }
    return false;
};

const handleChatMessage = ({ data }) => {
    if (data.uce_id && cancelledUceIds.value.has(data.uce_id)) {
        // If this is the final message for a cancelled exchange, clean up the cancelled ID
        if (data.role === "assistant") {
            cancelledUceIds.value.delete(data.uce_id);
        }
        return;
    }
    // Do whatever UI state needed, add to message queue
    // Clear the slow-response timer on every assistant chat_message. The old guard here
    // was `if (waiting_for_assistant.value)`, which is a vestige of a coarser whole-function
    // gate and failed in tool-call flows where an intermediate chat_message had already
    // flipped waiting_for_assistant=false — letting the last-armed partial timer survive
    // to fire under the finalized response. Gate on role so user-role re-emits don't touch
    // timer state they didn't own.
    if (data.role === "assistant") {
        clearSlowResponseTimeout();
    }

    // Cancel any pending RAF to prevent it from running after we set the final lines
    // This avoids a race condition where the RAF callback would access deleted streaming_content
    if (partialUpdateRafId) {
        cancelAnimationFrame(partialUpdateRafId);
        partialUpdateRafId = null;
    }

    const m = getMessageById(data.message_id, data.temp_id);

    if (m) {
        m.lines = data.lines;
        // Clean up streaming content now that we have the final parsed lines
        delete m.streaming_content;

        // re-fetches connections via inertia if the message has tagged a person
        if (messageHasActions(m, [ACTION.TAGGED_PERSON])) {
            router.reload({
                only: ["connections"],
                preserveState: true,
                preserveScroll: true,
            });
        }

        if (data.message_id) {
            m.id = data.message_id;
        }
        if (data.chat_message_id) {
            m.chat_message_id = data.chat_message_id;
        }
        if (data.chat_message_uuid) {
            m.chat_message_uuid = data.chat_message_uuid;
        }
        if (data.feedback_target !== undefined) {
            m.feedback_target = data.feedback_target;
        }
    } else if (data.temp_id && pendingSoftDeleteTempIds.value.has(data.temp_id)) {
        pendingSoftDeleteTempIds.value.delete(data.temp_id);
        if (data.chat_message_uuid) {
            fetchApi(`/v1/chat/messages/${data.chat_message_uuid}/edit`, { method: "POST" }).catch((err) => {
                logError(err, { context: { operation: "deferred_stop_soft_delete" } });
            });
        }
    } else {
        messages.value.push(data);
    }

    openArtifactFromLiveMessage(data);
    void announceMyDevelopmentGoal(data);

    if (data.role === "assistant") {
        // A tool-call message is an intermediate assistant turn. Keep the exchange busy
        // while the tool card shows its own progress, then wait for the follow-up response.
        waiting_for_assistant.value = messageHasActions(data, [ACTION.TOOL_CALL]);
        // Reset thinking states when complete message arrives
        is_waiting_for_initial_response.value = false;
        is_thinking.value = false;

        logUserInteraction("coach_message_ended", { uce_id: data.uce_id }, cs_uuid);
        // note: rendered event used to be after message finished animating, we don't animate anymore so this has been moved here
        logUserInteraction("coach_message_rendered", { uce_id: data.uce_id }, cs_uuid);

        tagConnectionsOnAssistantMessage(m, data.chat_message_uuid);
    } else if (data.role === "tool" && data.uce_id) {
        // A goal draft, goal update card, or team picker is the terminal, actionable response for
        // this turn, so its buttons work without waiting for more from Nadia.
        if (isDevelopmentGoalDraftMessage(data) || isDevelopmentGoalUpdateCardMessage(data) || isTeamPickerMessage(data)) {
            resetAssistantResponseState();
        } else {
            // Tool results are always followed by another assistant turn. The completed tool
            // card is static, so bridge that otherwise blank interval with the normal loader.
            waiting_for_assistant.value = true;
            is_waiting_for_initial_response.value = true;
            is_thinking.value = false;
        }
    }
};

// An earlier message changed server-side (e.g. a typed correction dismissed its tag card). Only its lines
// are refreshed: unlike chat_message, this never ends the reply in flight.
const handleChatMessageUpdated = ({ data }) => {
    const m = getMessageById(data.message_id);
    if (m) {
        m.lines = data.lines;
    }
};

const tagConnectionsOnAssistantMessage = async (message, messageUuid) => {
    if (!memory_connections_enabled) {
        return;
    }
    if (!message || !messageUuid) {
        return;
    }
    // Defensively suppress the slow-response banner during our known-good async call —
    // we don't want "gather my thoughts" appearing while labels pop in.
    clearSlowResponseTimeout();
    try {
        const response = await fetchApi(`/v1/chat/messages/${messageUuid}/evaluate-and-tag-connections`, { method: "POST" });
        if (!response.ok) {
            return;
        }
        const body = await response.json();
        if (!body?.actions?.length) {
            return;
        }

        message.lines = [...(message.lines ?? []), ...body.actions];

        router.reload({
            only: ["connections"],
            preserveState: true,
            preserveScroll: true,
        });
    } catch (err) {
        logError(err);
    }
};

const handleChatWarning = (data) => {
    chatError.value = data.data.message;
};

const handleChatError = (data) => {
    resetAssistantResponseState();
    chatError.value = data.data.message;
};

// The streamed reply failed its output guardrail check, so it was never delivered as a chat_message. Drop its
// streaming bubble; the exchange stays busy until the replacement chat_message arrives.
const handleChatMessageRetracted = ({ data }) => {
    const index = messages.value.findIndex((m) => m.message_id == data.temp_id);
    if (index === -1) {
        return;
    }
    const [retracted] = messages.value.splice(index, 1);
    emitter.emit(CHAT_EVENT.CANCEL_MESSAGE_DICTATION, String(retracted.message_id));
    void logUserInteraction(
        AmplitudeEventNames.ASSISTANT_MESSAGE_RETRACTED,
        { uce_id: retracted.uce_id ?? userCoachExchangeId.value, guardrail_keys: data.guardrail_keys ?? [] },
        cs_uuid,
    );
    is_waiting_for_initial_response.value = true;
};

const handlePiiRejected = (data) => {
    // Remove the optimistic user message and restore its content to the input for editing
    const tempId = data.data?.temp_id;
    let rejectedContent = "";
    let rejectedMentions = [];
    if (tempId) {
        const idx = messages.value.findIndex((m) => m.temp_id === tempId);
        if (idx !== -1) {
            rejectedContent = messages.value[idx].content || "";
            rejectedMentions = messages.value[idx].mentions ?? [];
            messages.value.splice(idx, 1);
        }
    }
    // Reset chat state based on rejection:
    waiting_for_assistant.value = false;
    is_waiting_for_initial_response.value = false;
    clearSlowResponseTimeout();
    chatError.value = data.data?.reason || t("chat.detail.errors.piiBlocked");
    if (rejectedContent) {
        emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, { content: rejectedContent, mentions: rejectedMentions });
    }
};

const handleChatPartialAddition = (data) => {
    if (data.uce_id && cancelledUceIds.value.has(data.uce_id)) {
        return;
    }
    waiting_for_assistant.value = true;
    clearSlowResponseTimeout();

    // Clear thinking states as soon as we start receiving visible content
    is_waiting_for_initial_response.value = false;
    is_thinking.value = false;

    const m = getMessageById(data.data["message_id"]);

    if (m) {
        // Delta mode: accumulate the delta content for better streaming performance
        if (data.data["delta_content"] !== undefined) {
            // Accumulate content immediately (no data loss)
            m.streaming_content = (m.streaming_content || "") + data.data["delta_content"];

            // Throttle reactive lines update to animation frames (~60fps max)
            // This reduces CPU overhead from excessive DOM updates during fast streaming
            if (!partialUpdateRafId) {
                partialUpdateRafId = requestAnimationFrame(() => {
                    partialUpdateRafId = null;
                    m.lines = [{ type: "md", content: m.streaming_content }];
                });
            }
        } else {
            // Legacy mode: full lines replacement (backwards compatibility)
            m.lines = data.data["lines"];
        }
    } else {
        const newMessage = { ...data.data };
        // Delta mode: initialize streaming_content from delta
        if (newMessage["delta_content"] !== undefined) {
            newMessage.streaming_content = newMessage["delta_content"];
            newMessage.lines = [{ type: "md", content: newMessage.streaming_content }];
            delete newMessage["delta_content"];
        }
        messages.value.push(newMessage);
        if (newMessage["role"] == "assistant") {
            logUserInteraction("coach_message_start", { uce_id: newMessage["uce_id"] }, cs_uuid);
        }
    }

    /*
     * We may receive many partial additions in quick succession,
     * often times faster than we can notice. We throttle the automatic
     * scrolling accordingly to avoid jitter.
     */
    startSlowResponseTimeout({ uce_id: data.uce_id });
};

const handleToolCallStatus = (data) => {
    const toolCallId = data.data["tool_call_id"];
    const statusText = data.data["status_text"];
    if (!toolCallId || !statusText) {
        return;
    }
    toolCallStatusText.set(toolCallId, statusText);
};

const handleTranscriptionEvent = (transcription) => {
    if (!waiting_for_transcript.value) {
        return;
    }

    waiting_for_transcript.value = false;
    show_slow_transcription.value = false;
    dictated_in_this_message.value = true;
    logUserInteraction("transcription_ended", { uce_id: userCoachExchangeId.value }, cs_uuid);
    // Held like typed text while a reply is still in flight, so a voice message can't abort it.
    handleSendMessage({ content: transcription, files: useChatStore.files, uce_id: userCoachExchangeId.value });
    useChatStore.removeAllFiles();
};

const updateActionState = (data) => {
    const { action_state, action_params, message_id, lineIdx, ...rest } = data;
    const existingLine = getMessageLine(message_id, lineIdx);
    const line = { ...existingLine };
    if (action_state) {
        line.action_state = {
            ...line.action_state,
            ...action_state,
        };
    }

    if (action_params) {
        line.action_params = {
            ...line.action_params,
            ...action_params,
        };
    }

    for (let x in rest) {
        line[x] = rest[x];
    }

    messages.value = messages.value.map((message) => {
        if (message_id === message.chat_message_id) {
            // First optimistic write for an action the server hasn't echoed back yet:
            // there is no line to patch, so append it (callers supply type/action_name).
            if (!existingLine && lineIdx === undefined && line.type === "action" && line.action_name) {
                return { ...message, lines: [...message.lines, line] };
            }
            return {
                ...message,
                lines: message.lines.map((l, idx) => {
                    if (idx === lineIdx) {
                        return line;
                    } else {
                        return l;
                    }
                }),
            };
        } else {
            return message;
        }
    });
};

provide("updateActionState", updateActionState);

const handleFollowupChange = (data) => {
    updateActionState(data);
    $sendEvent("action", { data: { type: ACTION.FOLLOW_UP, payload: data } });
};

const handleChooseFollowup = (data) => {
    updateActionState(data);
    $sendEvent("action", { data: { type: ACTION.FOLLOW_UP, payload: data } });
    markOnboardChecklistItemComplete(ONBOARDING_TASK.FOLLOWUP);
};

const handleRolePlayAdjust = (payload) => {
    if (payload.action_params?.adjustment) {
        rolePlayAdjust.value = payload.action_params.adjustment;
    }
    // type/action_name let updateActionState append the line when the server
    // hasn't echoed one back yet, so the widget's label reflects the click.
    updateActionState({ ...payload, type: "action", action_name: ACTION.ROLE_PLAY_ADJUST });
    // ActionHandler looks up ChatMessage by PK. Streaming rows use a timestamp
    // string, and temp user rows have no id yet — posting those 500s.
    if (typeof payload.message_id !== "number" || payload.message_id <= 0) {
        return;
    }
    $sendEvent("action", { data: { type: ACTION.ROLE_PLAY_ADJUST, payload } }).catch((error) => {
        console.error("Failed to save role play style", error);
        emitter.emit("error_notification", t("chat.detail.errors.rolePlayStyleSaveFailed"));
    });
};

const handleProfileQuestion = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: "profile_question", payload } });
};

// Saves run one at a time: the server rewrites the whole action_items JSON per request, so overlapping
// requests could land out of order and leave the stored state different from what the card shows.
let actionItemsSaveQueue = Promise.resolve();

const handleActionItems = (payload) => {
    updateActionState(payload);
    actionItemsSaveQueue = actionItemsSaveQueue
        .then(() => $sendEvent("action", { data: { type: "action_items", payload } }))
        .catch((error) => {
            console.error("Failed to save action item", error);
            const item = payload.action_params?.action_items?.[payload.item_index];
            // The request may have committed before the response was lost. Reverting is safe because a retry
            // sends the explicit state, so it cannot undo a save that did land.
            const unchangedSinceClick = typeof payload.checked === "boolean" ? item?.checked === payload.checked : true;
            if (payload.subaction === "check_item" && item && unchangedSinceClick) {
                item.checked = !item.checked;
            }
            emitter.emit("error_notification", t("chat.detail.errors.actionItemSaveUnconfirmed"));
        });
};

const handleVoiceNudge = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: "voice_nudge", payload } });
};

// Persists the PM picker's clicks (for the session debug view) and its collapsed state,
// so returning to the chat doesn't re-present the wizard for a choice the user already made.
const handlePmPicker = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: ACTION.PM_PICKER, payload } });
};

const msgBuffer = ref(null);
const isBufferingMessage = computed(() => !!msgBuffer.value || showStartLoadingMessage.value);
const reviewDocumentUploadBusy = ref(false);
provide("reviewDocumentUpload", {
    setBusy: (busy) => {
        reviewDocumentUploadBusy.value = busy;
    },
    enabled: computed(() => document_upload_enabled && performance_management_flow_improvements_enabled && reviewDocumentCardAllowed.value),
    context: computed(() => ({ sessionUuid: cs_uuid, reviewConversationId: performance_review_context?.review_conversation_id })),
    remaining: computed(() => Math.max(0, FILE_UPLOAD_LIMIT - fileUploadCount.value - useChatStore.files.length)),
    files: computed(() => useChatStore.files),
    addFiles: (files) => files.forEach((file) => useChatStore.addFile(file)),
    removeFile: (file) => useChatStore.removeAttachment(file),
    focus: () => chatMessageControls.value?.focus(),
});
const handleReviewDocumentCard = (card) => {
    if (card.message_id == null || card.index == null || !card.params) {
        return;
    }
    const message = messages.value.find((message) => message.chat_message_id === card.message_id);
    if (!message) {
        return;
    }
    const lineIdx = message.lines.findIndex((line) => line.type === "action" && line.index === card.index);
    updateActionState({
        message_id: card.message_id,
        ...(lineIdx >= 0 ? { lineIdx } : {}),
        type: "action",
        action_name: ACTION.REVIEW_DOCUMENT_UPLOAD,
        index: card.index,
        action_params: card.params,
        action_state: { dismissed: card.dismissed, minimized: false, submitted: false },
    });
};

watch([waiting_for_assistant, isPerformancePickerOpen], ([waiting, pickerOpen]) => {
    if (!waiting && !pickerOpen && msgBuffer.value != null && sendBufferedMessage(msgBuffer.value)) {
        msgBuffer.value = null;
    }
});
const sendBufferedMessage = ({ content, files, uce_id, mentions }) => {
    const sent = sendMessage(content, { promptSwitchCallCode: promptSwitchCallCode.value, rolePlayAdjust: rolePlayAdjust.value, files, uce_id, mentions });
    if (sent) {
        promptSwitchCallCode.value = null;
        rolePlayAdjust.value = null;
    }
    return sent;
};

const handleSendMessage = ({ content, files, uce_id, mentions }) => {
    if (isPerformancePickerOpen.value) {
        return;
    }
    if (waiting_for_assistant.value) {
        if (content.length > 0 || files.length > 0) {
            emitter.emit(CHAT_EVENT.USER_MESSAGE_SEND);
        }
        msgBuffer.value = { content, files, uce_id, mentions };
    } else {
        sendBufferedMessage({ content, files, uce_id, mentions });
    }
};

// Track cancelled UCE IDs so late-arriving streaming events are ignored.
// Capped at 20 entries to prevent unbounded growth if the backend never
// sends a final assistant message for a cancelled exchange.
const MAX_CANCELLED_UCE_IDS = 20;
const cancelledUceIds = ref(new Set());
const pendingSoftDeleteTempIds = ref(new Set());

const requestStopGenerating = () => emitter.emit(CHAT_EVENT.STOP_GENERATING);

const handleStopGenerating = () => {
    // Evict oldest entry if at capacity
    if (cancelledUceIds.value.size >= MAX_CANCELLED_UCE_IDS) {
        const oldest = cancelledUceIds.value.values().next().value;
        cancelledUceIds.value.delete(oldest);
    }
    // Capture the current UCE ID before resetting so we can ignore late-arriving chunks
    cancelledUceIds.value.add(userCoachExchangeId.value);

    // Abort the in-flight HTTP streaming request so the server cancels the handler
    socketClient.value?.abortActiveRequest();

    // Remove the streaming assistant message (the partial response) if present
    const lastMsg = messages.value[messages.value.length - 1];
    if (lastMsg && lastMsg.role === "assistant" && !lastMsg.id) {
        const dictationMessageId = lastMsg.message_id ?? lastMsg.temp_id;
        if (dictationMessageId) {
            emitter.emit(CHAT_EVENT.CANCEL_MESSAGE_DICTATION, String(dictationMessageId));
        }
        messages.value.pop();
    }

    // Remove the user message and restore its text to the input editor
    const userMsg = messages.value[messages.value.length - 1];
    if (userMsg && userMsg.role === "user") {
        messages.value.pop();
        const mdLines = userMsg.lines?.filter((l) => l.type === "md") ?? [];
        const restoredContent = mdLines.map((l) => ("content" in l ? l.content : "")).join("\n");
        if (restoredContent) {
            emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, {
                content: restoredContent,
                mentions: userMsg.mentions ?? userMsg.lines?.find((line) => line.action_name === "composer_mentions")?.action_params?.mentions ?? [],
            });
        }
        // Soft-delete from backend so it doesn't reappear on refresh
        if (userMsg.chat_message_uuid) {
            fetchApi(`/v1/chat/messages/${userMsg.chat_message_uuid}/edit`, { method: "POST" }).catch((err) => {
                logError(err, { context: { operation: "stop_generating_soft_delete" } });
            });
        } else if (userMsg.temp_id) {
            pendingSoftDeleteTempIds.value.add(userMsg.temp_id);
        }
    }

    // Reset all waiting/thinking state
    waiting_for_assistant.value = false;
    is_waiting_for_initial_response.value = false;
    is_thinking.value = false;
    show_slow_response.value = false;
    clearSlowResponseTimeout();
    msgBuffer.value = null;

    logUserInteraction("stop_generating", { uce_id: userCoachExchangeId.value }, cs_uuid);
};

const handleEditMessage = ({ messageIdx, content, chatMessageUuid }) => {
    // If currently generating, stop first — it already removes messages,
    // restores text, and soft-deletes, so we can return early.
    if (waiting_for_assistant.value) {
        requestStopGenerating();
        return;
    }

    const editedMessage = messages.value[messageIdx];
    const mentions = editedMessage?.mentions ?? editedMessage?.lines?.find((line) => line.action_name === "composer_mentions")?.action_params?.mentions ?? [];
    // Remove the edited message and all subsequent messages from the frontend
    messages.value.splice(messageIdx);

    // Ensure the composer is visible (it may be hidden behind "Start New Session" controls)
    returnToChatChoiceMade.value = true;

    // Restore the message text to the input box and scroll down
    if (content) {
        emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, { content, mentions });
        scrollToBottom();
    }

    // Soft-delete the messages on the backend so they don't reappear on refresh
    if (chatMessageUuid) {
        fetchApi(`/v1/chat/messages/${chatMessageUuid}/edit`, { method: "POST" }).catch((err) => {
            logError(err, { context: { operation: "edit_message_soft_delete" } });
        });
    }

    logUserInteraction("edit_message", { message_idx: messageIdx }, cs_uuid);
};

const handleOnboardingFollowup = () => {
    markOnboardChecklistItemComplete(ONBOARDING_TASK.FOLLOWUP);
};

const handleWrapUpChat = () => {
    logUserInteraction(CHAT_EVENT.WRAP_UP_CHAT, {}, cs_uuid);
    isChatWrapUpDialogOpen.value = true;
};

const handleCloseChatWrapUpDialog = () => {
    isChatWrapUpDialogOpen.value = false;
};

const handleOpenEmailSummary = () => {
    logUserInteraction(CHAT_EVENT.OPEN_SESSION_SUMMARY, {}, cs_uuid);
    isEmailSummaryDialogOpen.value = true;
};

/**
 * Send a chat message to the backend via WebSocket
 * @param {string} content - The message content to send
 * @param {Object} options - Optional parameters
 * @param {string} [options.promptSwitchCallCode] - Code to switch prompts (e.g., "Role_Play", "triage")
 * @param {string} [options.rolePlayAdjust] - Adjustment parameter for ongoing role play
 * @param {Array} [options.files] - Array of file objects to attach to the message
 * @param {Object|false} [options.rolePlayContext] - Role play context with name, relationship, personality, context fields.
 *                                                    Pass false to explicitly dismiss role play context.
 * @param {string} [options.uce_id] - User coach exchange ID for tracking
 */
const sendMessage = (
    content,
    { promptSwitchCallCode, rolePlayAdjust, files = [], mentions = [], rolePlayContext, uce_id, collaborationConnectionId, teamPerspectiveTeamId, teamPerspectiveToolCallId } = {},
) => {
    if (teamPerspectiveTeamId && waiting_for_assistant.value) {
        return false;
    }
    if (isPerformancePickerOpen.value || isDevelopmentGoalChoicePending.value || savingDevelopmentGoalUpdateMessageId.value !== null) {
        return false;
    }

    uce_id = uce_id ?? cycleUserCoachExchangeId();
    if (content.length > 0 || files.length > 0) {
        emitter.emit(CHAT_EVENT.USER_MESSAGE_SEND);
        waiting_for_assistant.value = true;
        is_waiting_for_initial_response.value = true;
        has_taken_action.value = true;
        // Temporary message so the backend can send back data about this message
        const temp_id = `${Date.now()}${cs_id}`;
        const message = {
            temp_id: temp_id,
            uce_id,
            content,
            role: "user",
            dictation_on: useChatStore.dictationOn,
            dictated: dictated_in_this_message.value,
            files,
            ...(performance_review_context?.review_conversation_id ? { review_conversation_id: performance_review_context.review_conversation_id } : {}),
            ...(mentions.length ? { mentions } : {}),
            collaboration_connection_id: collaborationConnectionId,
            team_perspective_team_id: teamPerspectiveTeamId,
            team_perspective_tool_call_id: teamPerspectiveToolCallId,
        };
        if (promptSwitchCallCode) {
            message.prompt_switch_call_code = promptSwitchCallCode;
        }
        if (rolePlayAdjust) {
            message.role_play_adjust = rolePlayAdjust;
        }
        if (rolePlayContext) {
            message.role_play_context = rolePlayContext;
        }
        $sendSocketEvent("user_chat_message", message, socketClient.value);

        // calculates diff in last message and current, if greater than 24 hours sends an additional property in the event
        const RETURN_SESSION_HOURS = 24;
        const lastMessage = messages.value[messages.value.length - 1];
        const lastMessageDateTime = lastMessage?.created_at ? convertToDateTime(lastMessage.created_at) : undefined;
        const differenceInHours = Math.abs(lastMessageDateTime?.diffNow("hours").hours ?? 0);

        logUserInteraction(
            AmplitudeEventNames.USER_MESSAGE_SENT,
            {
                uce_id,
                mic: dictated_in_this_message.value,
                coaching_session_id: cs_uuid,
                chat_source,
                ...(differenceInHours > RETURN_SESSION_HOURS && { is_return_session: true }),
            },
            cs_uuid,
        );

        message.lines = [{ type: "md", content }, ...(mentions.length ? [{ type: "action", action_name: "composer_mentions", action_params: { mentions } }] : [])];
        // We don't want to show the role play message
        if (!rolePlayContext) {
            messages.value.push({ ...message, files });
        }
        scrollToBottom();

        dictated_in_this_message.value = false;

        const userMessagesCount = messages.value.filter((message) => message.role === "user").length;
        if (userMessagesCount === 3) {
            markOnboardChecklistItemComplete(ONBOARDING_TASK.GUIDANCE);
        }

        startSlowResponseTimeout({ timeout_ms: 4000, uce_id });
        return true;
    }
    return false;
};
provide("sendMessage", sendMessage);
// Rich-artifact action buttons use this one: it queues behind an in-flight reply instead
// of racing the streaming client.
provide("handleSendMessage", handleSendMessage);
provide(
    "teamPerspectiveBusy",
    computed(() => waiting_for_assistant.value),
);
provide(
    "collaborationAnalysisBusy",
    computed(() => waiting_for_assistant.value),
);

const keepEditingDevelopmentGoal = (message, lineIdx) => {
    const line = message.lines[lineIdx];
    if (line !== latestDevelopmentGoalAction.value?.line || waiting_for_assistant.value) {
        return;
    }

    logDevelopmentGoalCardAction(DEVELOPMENT_GOAL_CARD_ACTION.KEEP_EDITING);
    dismissDevelopmentGoalLockIn(message, lineIdx);
};

// Only Nadia's latest proposed goal change can be confirmed. It stays open through replies, since Nadia may point back to it.
const activeDevelopmentGoalUpdate = computed(() => {
    const latest = pluckMultipleActions(messages.value, ACTION.PROPOSE_DEVELOPMENT_GOAL_UPDATE)
        .filter(({ line }) => line.action_params?.goal_update)
        .at(-1);
    const isOpen = latest && !latest.line.action_state?.dismissed && !latest.line.action_params.saved_goal;
    return isOpen ? latest : null;
});
const savingDevelopmentGoalUpdateMessageId = ref(null);
const developmentGoalUpdateAnnouncement = computed(() => {
    const proposal = activeDevelopmentGoalUpdate.value?.line.action_params.goal_update;
    return proposal ? `${proposal.title}. ${developmentGoalUpdateQuestion(t, proposal).replaceAll("**", "")}` : "";
});
const developmentGoalUpdateActions = useTemplateRef("developmentGoalUpdateActions");
const chatMessageControls = useTemplateRef("chatMessageControls");
watch(isPerformancePickerOpen, async (open, wasOpen) => {
    if (wasOpen && !open) {
        await nextTick();
        chatMessageControls.value?.focus();
    }
});

const DAY_MS = 24 * 60 * 60 * 1000;
// Analytics gets how far the date moved, not the dates themselves, which are the person's goal data.
const targetDateShiftDays = ({ current_target_date, target_date }) =>
    current_target_date ? Math.round((Date.parse(target_date) - Date.parse(current_target_date)) / DAY_MS) : null;

const logDevelopmentGoalUpdateClick = (proposal, choice) => {
    const targetDateShift = proposal.change === "target_date" ? { target_date_shift_days: targetDateShiftDays(proposal) } : {};
    void logUserInteraction(
        AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GOAL_UPDATE_CLICKED,
        { goal_update_choice: choice, goal_update_type: proposal.change, ...targetDateShift },
        cs_uuid,
    );
};

const confirmDevelopmentGoalUpdate = async () => {
    const active = activeDevelopmentGoalUpdate.value;
    if (!active || waiting_for_assistant.value || savingDevelopmentGoalUpdateMessageId.value !== null) {
        return;
    }

    const { message, line } = active;
    logDevelopmentGoalUpdateClick(line.action_params.goal_update, "confirm");

    const goalUpdateAction = (subaction) => ({
        data: { type: ACTION.PROPOSE_DEVELOPMENT_GOAL_UPDATE, payload: { message_id: message.chat_message_id, subaction }, sync: true },
    });

    savingDevelopmentGoalUpdateMessageId.value = message.chat_message_id;
    chatError.value = null;
    let succeeded = false;
    try {
        // The server confirms the conversation, card and goal are still eligible before anything is written.
        const checked = await $sendEvent("action", goalUpdateAction("check")).catch(() => null);
        if (!checked) {
            // A request that never got an answer says nothing about the card, so the person can simply retry.
            showChatToast(t("developmentJourneys.goalProgress.goalUpdate.saveError"));
            return;
        }
        if (!checked.success) {
            chatError.value = t("developmentJourneys.goalProgress.goalUpdate.unavailableError");
            return;
        }

        // An already recorded card skips the write. Otherwise the server re-reads the goal before
        // recording, so a save whose response was lost is still recognised.
        const saved = checked.completed || (await saveDevelopmentGoalUpdate(line.action_params.goal_update));
        waiting_for_assistant.value = true;
        is_waiting_for_initial_response.value = true;
        const recorded = await sendGoalUpdateConfirmation(goalUpdateAction("confirm"));
        if (!recorded) {
            resetAssistantResponseState();
            chatError.value = saved ? t("developmentJourneys.goalProgress.goalUpdate.recordError") : t("developmentJourneys.goalProgress.goalUpdate.saveError");
            return;
        }
        succeeded = true;
        if (sendStreamingEvent) {
            // The stream ends after Nadia's reply, or at once when another tab already recorded the card.
            resetAssistantResponseState();
        }
    } finally {
        savingDevelopmentGoalUpdateMessageId.value = null;
        // The buttons are hidden while saving, so after a failure focus returns to Confirm for a keyboard retry,
        // and after a save it moves on to the message box unless the person has already put it somewhere.
        if (!succeeded && activeDevelopmentGoalUpdate.value) {
            await nextTick();
            developmentGoalUpdateActions.value?.focusLockIn();
        } else if (succeeded && [null, globalThis.document.body].includes(globalThis.document.activeElement)) {
            await nextTick();
            chatMessageControls.value?.focus();
        }
    }
};

// Recording the saved change starts Nadia's reply to it, so it streams when the page can.
const sendGoalUpdateConfirmation = async (action) => {
    try {
        return sendStreamingEvent ? await sendStreamingEvent("action", action) : (await $sendEvent("action", action))?.success;
    } catch {
        return false;
    }
};

const cancelDevelopmentGoalUpdate = () => {
    const active = activeDevelopmentGoalUpdate.value;
    if (!active || waiting_for_assistant.value || savingDevelopmentGoalUpdateMessageId.value !== null) {
        return;
    }

    logDevelopmentGoalUpdateClick(active.line.action_params.goal_update, "cancel");
    const payload = { message_id: active.message.chat_message_id, lineIdx: active.lineIdx, action_state: { dismissed: true } };
    updateActionState(payload);
    // The focused Cancel button leaves with the card, so focus moves on to the message box.
    void nextTick(() => chatMessageControls.value?.focus());
    $sendEvent("action", { data: { type: ACTION.PROPOSE_DEVELOPMENT_GOAL_UPDATE, payload } }).catch((error) => {
        logError(error, { context: { operation: "cancel_development_goal_update" } });
    });
};

const handleKeyDown = () => {
    if (chatError.value) {
        chatError.value = null;
    }
};

const handleTranscribeRecording = async ({ blob, recordingTime, audioStats }) => {
    const eventData = {
        transcribe_session_start: Date.now(),
        blob_size: blob.size,
        blob_type: blob.type,
        recording_time: recordingTime,
        user_agent: navigator.userAgent,
        uce_id: cycleUserCoachExchangeId(),
        ...(audioStats && {
            audio_audible_pct: Math.round(audioStats.audiblePercentage * 1000) / 1000,
            audio_avg_level: Math.round(audioStats.avgLevel * 1000) / 1000,
            audio_peak_level: Math.round(audioStats.peakLevel * 1000) / 1000,
            audio_sample_count: audioStats.sampleCount,
            ...(audioStats.warmupMs != null && { audio_warmup_ms: audioStats.warmupMs }),
            ...(audioStats.initToStartMs != null && { init_to_start_ms: audioStats.initToStartMs }),
        }),
    };

    waiting_for_transcript.value = true;

    // Demoing feature!
    // Check if we have any remaining items to go through in our dev tools script
    if (devtoolsEnabled.value) {
        const chatScriptStorageKey = `chat_script_${first_prompt_id}`;
        const script = JSON.parse(window.localStorage.getItem(chatScriptStorageKey, "{}"));
        const messages = script?.messages || [];

        const messagesExist = messages.some((message) => message.message.length > 0);

        // Script index needs to be unique to the coaching session
        const scriptIndexKey = `chat_script_current_index_${cs_id}`;
        const currentScriptIndex = Number(window.localStorage.getItem(scriptIndexKey, 0));

        if (messagesExist && currentScriptIndex < messages.length) {
            const currentMessage = messages[currentScriptIndex].message;
            window.localStorage.setItem(scriptIndexKey, currentScriptIndex + 1);

            // Wait between 300 and 700ms to make it look like we're actually doing some work here
            setTimeout(
                () => {
                    sendMessage(currentMessage);
                    waiting_for_transcript.value = false;
                },
                getRandomInt(300, 700),
            );

            return;
        }
    }

    try {
        const transcript = await transcribeRecording({
            blob,
            onStart: () => {
                show_slow_transcription.value = false;
                logUserInteraction("transcription_request_start", eventData, cs_uuid);
            },
            onSlow: () => {
                show_slow_transcription.value = true;
                logUserInteraction("saw_slow_transcription_message", eventData, cs_uuid);
            },
            onError: (err) => {
                logUserInteraction("saw_transcription_error_message", eventData, cs_uuid);
                logUserInteraction("transcription_request_failed", eventData, cs_uuid);
                logError(err, {
                    context: { transcription: { cs_id, source: "ChatDetail" } },
                    tags: { error_type: "transcription", error_code: err.error_code },
                });
            },
        });
        logUserInteraction("transcription_request_succeeded", eventData, cs_uuid);
        handleTranscriptionEvent(transcript);
    } catch (err) {
        if (err.error_code === "no_speech_detected") {
            chatError.value = chatErrorMessages.value.NO_SPEECH_DETECTED;
            logUserInteraction("no_speech_detected", eventData, cs_uuid);
        } else {
            chatError.value = chatErrorMessages.value.TRANSCRIPTION_EXHAUSTED;
            logUserInteraction("transcription_request_retries_exhausted", eventData, cs_uuid);
        }
    } finally {
        waiting_for_transcript.value = false;
        show_slow_transcription.value = false;
    }
};

const handleRecordingComplete = (recording) => {
    markOnboardChecklistItemComplete(ONBOARDING_TASK.MIC);
    handleTranscribeRecording(recording);
};

const handleRecordingError = () => {
    chatError.value = chatErrorMessages.value.RECORDING_DEFAULT;
};

const handleRecordingStart = () => {
    chatError.value = null;
    has_taken_action.value = true;
};

const chatMessageListRef = useTemplateRef("chatMessageList");
const { bottom: mobileNavigationBottom } = useElementBounding(useTemplateRef("mobileNavigation"));
const { bottom: mobileSidebarControlsBottom } = useElementBounding(useTemplateRef("mobileSidebarControls"));

const scrollToBottom = () => {
    /*
     * Delegate scroll to the next tick as this function
     * is invoked at the same time that messages state is mutated.
     * Our scrolling logic depends on DOM elements, so we make sure
     * that this runs when Vue has reconciled the DOM state.
     */
    nextTick(() => {
        if (chatMessageListRef.value) {
            chatMessageListRef.value.scrollToLastMessage();
        }
    });
};

const startSlowResponseTimeout = ({ timeout_ms, ...event_data } = {}) => {
    clearSlowResponseTimeout();

    show_slow_response.value = false;
    slow_response_timeout.value = setTimeout(() => {
        startFailedResponseTimeout(event_data);
        show_slow_response.value = true;
        logUserInteraction("saw_slow_message", event_data, cs_uuid);
    }, timeout_ms ?? SLOW_MESSAGE_TIMEOUT_MS);
};

const clearSlowResponseTimeout = () => {
    clearFailedResponseTimeout();
    show_slow_response.value = false;
    clearTimeout(slow_response_timeout.value);
};

const startFailedResponseTimeout = ({ timeout_ms, ...event_data } = {}) => {
    clearFailedResponseTimeout();

    failed_response_timeout.value = setTimeout(() => {
        show_failed_response.value = true;
        logUserInteraction("saw_failed_message", event_data, cs_uuid);
    }, timeout_ms ?? FAILED_MESSAGE_TIMEOUT_MS);
};

const clearFailedResponseTimeout = () => {
    show_failed_response.value = false;
    clearTimeout(failed_response_timeout.value);
};

const markOnboardChecklistItemComplete = (taskId) => {
    emitter.emit(CHAT_EVENT.MARK_ONBOARD_CHECKLIST, { taskId });
};

const handleToggleWidgetSidebar = () => {
    showWidgetSidebar.value = !showWidgetSidebar.value;
    // The document pane and the expanded Notes list never share the row: both open at once
    // leave no usable chat width on a narrow desktop (Notes 325px + pane 360px minimum).
    if (showWidgetSidebar.value) {
        closeArtifact();
    }
};

const handleFailedResponseReload = () => {
    document.location.reload();
};

const handleRolePlayStarted = (payload) => {
    useChatStore.setRolePlay(payload.data);
};

const handleEndRolePlay = () => {
    if (useChatStore.rolePlay?.hashid) {
        router.put(
            `/role-play/summary/${useChatStore.rolePlay.hashid}/`,
            {},
            {
                headers: {
                    "X-CSRFToken": getCookie("csrftoken"),
                },
                onSuccess: () => {
                    logUserInteraction("role_play_summary_sent", { id: useChatStore.rolePlay.id }, cs_uuid);
                    useChatStore.setRolePlay({});
                },
                onError: () => {
                    chatError.value = t("chat.detail.errors.endRolePlayFailed");
                    logError(`Failed to navigate to role play summary: ${useChatStore.rolePlay.id}`);
                },
            },
        );
    }
};

/**
 * Handle starting a role play session from within the chat
 * @param {Object} options
 * @param {Object} options.rolePlayContext - Role play setup context containing:
 * @param {string} options.rolePlayContext.name - Name of the person being role-played
 * @param {string} options.rolePlayContext.relationship - Relationship to the user (e.g., "My manager")
 * @param {string} options.rolePlayContext.personality - Personality traits of the person
 * @param {string} options.rolePlayContext.context - Scenario/situation context for the role play
 */
const handleInChatRolePlayStarted = ({ rolePlayContext }) => {
    const rolePlayMessage = t("chat.detail.rolePlayStartMessage");
    sendMessage(rolePlayMessage, { rolePlayContext, files: useChatStore.files });
};

/**
 * Handle canceling an ongoing role play session
 * Sends a message with rolePlayContext: false to signal cancellation (gets converted to false/None on backend)
 */
const handleInChatRolePlayCanceled = () => {
    const rolePlayCanceledMessage = t("chat.detail.rolePlayCancelMessage");
    sendMessage(rolePlayCanceledMessage, { rolePlayContext: false, files: useChatStore.files });
};

const handleErrorNotification = (message) => {
    chatError.value = message;
    chatSuccess.value = null; // Clear success message when showing error
};

const handleSuccessNotification = (message) => {
    chatSuccess.value = message;
    chatError.value = null; // Clear error message when showing success
};

const handleInitialResponseStarted = () => {
    is_waiting_for_initial_response.value = false;
};

const handleChatSendDeduplicated = () => {
    is_thinking.value = false;
    is_waiting_for_initial_response.value = false;
};

const handleThinkingStarted = () => {
    is_thinking.value = true;
};

const handleVisibleContentStarted = () => {
    is_thinking.value = false;
};

const handleContinueChat = () => {
    returnToChatChoiceMade.value = true;
};

const handleGoBack = () => {
    if (is_development_journeys_chat && development_journeys_url) {
        returnToDevelopmentJourneys();
        return;
    }

    if (globalThis.history && globalThis.history.length > 1) {
        globalThis.history.back();
    } else {
        router.visit("/coach/history");
    }
};

// After the keyboard finishes animating in and the layout has reflowed,
// anchor the latest assistant message below the top fade. One animation
// frame lets the new `clientHeight` become observable before scrolling.
const { settledKeyboardHeight } = useKeyboard();
watch(settledKeyboardHeight, (next) => {
    if (next > 0) {
        requestAnimationFrame(() => {
            chatMessageListRef.value?.pinLatestAssistantToTop();
        });
    }
});
</script>

<style scoped>
/* Empty notice/banner wrappers keep their margin (Vue leaves v-if comment placeholders, so
   `:empty` doesn't match), which leaves a dead band above the input in the overlay. */
.composer-region > div:not(:has(*)) {
    margin-bottom: 0;
}
</style>
